import type { Dictionary } from './types.js';
import { completionEn } from './completion.js';
import { customerPaymentsTranslations } from './customerPayments.js';

/**
 * English — secondary locale, a straight translation of he.ts.
 * `Dictionary` is derived from the Hebrew tree, so a missing or renamed key
 * here is a compile error. That is the guard that keeps locales in sync.
 */
export const en: Dictionary = {
  customerPayments: customerPaymentsTranslations.en,
  completion: completionEn,
  googleCalendar: {
    failedCount: 'Sync errors',
    confirmAccount: 'Confirm Google account',
    confirmAccountHint: 'Check that this is the personal Google account you want to connect.',
    confirmConnection: 'Confirm and connect calendar',
    flowMismatch: 'This connection started in another window or account. Start again here.',
    title: 'Your Google Calendar',
    description: 'Sync your Worki reminders and meetings you book to your personal Google calendar. Changes in Google are not imported into Worki.',
    connect: 'Connect Google Calendar',
    disconnect: 'Disconnect calendar',
    connected: 'Calendar connected',
    disconnected: 'Calendar not connected',
    unavailable: 'Google Calendar connection is not configured on this server.',
    pending: 'Waiting to sync',
    synced: 'Synced',
    error: 'Could not sync',
    retry: 'Try syncing again',
    oauthFailed: 'Calendar connection failed. Please try again.',
    oauthCancelled: 'Calendar connection was cancelled.',
    reconnectRequired: 'Reconnect your calendar to renew access.',
    disconnectHint: 'Synchronization will stop. Events already created in Google Calendar will remain there.',
    lastSynced: 'Last synchronized',
    durationHint: 'Reminders occupy 15 minutes and meetings 30 minutes in Google Calendar.',
    revokeFailed: 'Worki has stopped syncing. Google could not revoke access; you can remove Worki access in your Google account settings.',
    connectionCompleted: 'Google Calendar is connected. Return to Worki to view synchronization status.',
    returnToApp: 'Return to Worki',
  },
  calendar: {
    emptyDay: 'No meetings or reminders on this day',
    eventsCount: '{{count}} events',
    eventsCount_one: '1 event',
    eventsCount_two: '{{count}} events',
    eventsCount_other: '{{count}} events',
    selectedDay: 'Selected day events',
    reminderKind: 'Reminder',
    meetingKind: 'Meeting',
    title: 'Worki calendar', day: 'Day', week: 'Week', month: 'Month', today: 'Today',
    previous: 'Previous period', next: 'Next period', date: 'Date', view: 'View',
    empty: 'No meetings or reminders in this period', loadMore: 'Show more events',
    openReminder: 'Open reminder', openMeeting: 'Open meeting',
  },
  digitalDocument: {"checked": "Yes", "unchecked": "No", "toFill": "To be completed by the client", "enterValue": "Enter a value", "chooseOption": "Choose an option", "fillHint": "Complete the fields below before signing. Fields marked * are required.", "clearSignature": "Clear signature", "signHere": "Signature"},
  common: {
    print: 'Print',
    selectAllMatching: 'Select all pages ({{count}} items)',
    bulkSomeFailed: '{{count}} items could not be updated. They remain selected so you can review and try again.',
    bulkShareLimit: 'You can share up to {{count}} documents at a time. Narrow your selection before sharing.',
    bulkSelectionLimit: 'You can select up to {{count}} documents at a time. Narrow the filters and try again.',
    pickContact: 'Choose from contacts',
    contactUnavailable: 'Contacts could not be opened on this device.',
    contactPermissionDenied: 'Allow contacts access to choose a customer.',
    appName: 'Worki',
    save: 'Save',
    // For the toast that follows a save. `save` is the button label — showing it
    // again as the confirmation reads like an instruction, not a result.
    saved: 'Saved',
    deleted: 'Deleted',
    // Filter dropdowns sit in a toolbar with no room for a visible label, so the
    // unselected option carries it. Two neighbouring selects both reading 'All'
    // said nothing about what they filtered.
    allOf: '{{label}}: all',
    cancel: 'Cancel',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    view: 'View',
    create: 'Create',
    add: 'Add',
    remove: 'Remove',
    search: 'Search',
    share: 'Share',
    clear: 'Clear',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    moveUp: 'Move up',
    moveDown: 'Move down',
    no: 'No',
    loading: 'Loading…',
    empty: 'Nothing to show',
    error: 'Something went wrong',
    retry: 'Try again',
    explain: 'What is this?',
    selected_one: '1 selected',
    selected: '{{count}} selected',
    selectAll: 'Select all',
    clearSelection: 'Clear selection',
    selectMode: 'Selection mode',
    selectRow: 'Select row',
    actions: 'Actions',
    all: 'All',
    none: 'None',
    optional: 'optional',
    copy: 'Copy',
    copied: 'Copied',
    download: 'Download',
    document: 'Document',
    documentFailed: 'This document cannot be shown here',
    openInNewTab: 'Open in a new tab',
    openFullScreen: 'Full screen',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    send: 'Send',
    viewAll: 'View all',
    of: 'of',
    results: 'results',
    page: 'Page',
    logout: 'Log out',
    language: 'Language',
  },
  nav: {
    dashboard: 'Dashboard',
    activity: 'Activity log',
    notifications: 'Notifications',
    reminders: 'Reminders',
    menu: 'Menu',
    billing: 'Subscription',
    platform: 'Platform admin',
  },
  auth: {
    loginTitle: 'Sign in to Worki',
    loginSubtitle: 'Manage quotes, leads and invoices in one place',
    signupTitle: 'Create an account',
    signupSubtitle: 'Start running your business in minutes',
    email: 'Email',
    name: 'Full name',
    businessName: 'Business name',
    login: 'Sign in',
    signup: 'Sign up',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    otp: 'Login code',
    otpInvalid: 'The login code is incorrect or has expired',
    deleteConfirmMismatch: 'That is not the email address of this account',
    sendCode: 'Send me a login code',
    codeSent: 'We sent a login code to {{email}}',
    codeExpiry: 'The code is valid for 10 minutes and can be used once',
    changeEmail: 'Use a different email',
    signupCodeSent: 'We sent a confirmation code to {{email}}. Enter it to finish creating your account.',
    confirmSignup: 'Confirm and create account',
    editDetails: 'Change my details',
    phone: 'Mobile number',
    trade: 'Trade',
    tradePick: 'Choose a trade',
    tradeSearch: 'Search trades',
    tradeNoMatch: 'No trade by that name',
    taxId: 'Company / VAT number',
    taxIdHint: 'Your VAT or company number, 8 to 9 digits',
    city: 'Town',
    cityHint: 'Optional',
    termsPrefix: 'I have read and accept the ',
    termsLink: 'terms of use',
    termsAnd: ' and the ',
    privacyLink: 'privacy policy',
    google: 'Continue with Google',
    orEmail: 'or with your email',
    googleTitle: 'One more step',
    googleSubtitle: 'Signed in as {{email}}. Only the business details are left.',
    // 1.34 — the sign-up went through and the account waits for Worki to open it.
    // Above the card that carries `errors.accountPending`.
    signupPendingTitle: 'Sign-up complete',
    signupPendingSubtitle: 'You signed up with {{email}}',
    pitchTitle: 'Your whole business on one screen',
    pitchBody: 'Worki collects the enquiries, writes the quotes and follows the customers up — no spreadsheets, no chasing numbers.',
    pitchLeads: 'Nothing slips through',
    pitchLeadsBody: 'Calls, forms and messages land in one list with a status and a reminder.',
    pitchQuotes: 'A quote in a minute',
    pitchQuotesBody: 'Build it, send it by WhatsApp or email, and see when it was opened and approved.',
    pitchInvoices: 'Invoices file themselves',
    pitchInvoicesBody: 'Invoices arriving in the mailbox are read for you and land in the reports.',
    pitchFooter: 'Worki — small-business admin, in your own language',
  },
  platform: {
    searchPlaceholder: 'Search by name, email or mini-site address',
    allStatuses: 'All statuses',
    businesses: 'Businesses',
    active: 'Paying',
    trialing: 'On trial',
    pastDue: 'Past due',
    signupsThisMonth: 'Signed up this month',
    activeThisWeek: 'Active this week',
    business: 'Business',
    plan: 'Plan',
    status: 'Status',
    users: 'Users',
    customers: 'Customers',
    quotes: 'Quotes',
    lastActivity: 'Last activity',
    joined: 'Joined',
    never: 'Never',
    empty: 'No business matches this filter',
    detailTitle: 'Business record',
    subscriptionHistory: 'Subscription history',
    noSubscriptionHistory: 'Nothing has been billed on this account yet.',
    billingEvent: {
      checkout_started: 'Checkout started',
      cancelled: 'Subscription cancelled',
      subscription_activated: 'Subscription activated',
      subscription_cancelled: 'Cancelled at the provider',
      payment_succeeded: 'Payment received',
      payment_failed: 'Payment failed',
      ignored: 'Provider notice, subscription unchanged',
    },
    contact: 'Contact details',
    usage: 'Usage',
    inquiries: 'Inquiries',
    invoices: 'Invoices',
    galleries: 'Galleries',
    mailAccounts: 'Connected mailboxes',
    quotesThisMonth: 'Quotes this month',
    voiceActionsThisMonth: 'Quotes dictated to the AI this month',
    trialEnds: 'Trial ends',
    periodEnds: 'Next charge',
    provider: 'Payment provider',
    slug: 'Mini-site',
    readOnly: 'This screen only reads, apart from opening or closing an account’s access. It shows no customers, quotes or content belonging to a business — only counts.',
  },
  billing: {
    title: 'Subscription & billing',
    subtitle: 'The plans, and moving between them',
    currentPlan: 'Current plan',
    changePlan: 'Change plan',
    perMonth: 'per month',
    choose: 'Choose this plan',
    currentBadge: 'Your plan',
    status: {
      trialing: 'Trial',
      active: 'Active',
      past_due: 'Payment due',
      cancelled: 'Cancelled',
      expired: 'Expired',
    },
    trialEndsAt: 'Your trial ends on {{date}}',
    renewsAt: 'Renews on {{date}}',
    cancelledAt: 'Cancelled — active until {{date}}',
    pastDueNotice: 'The last payment failed. Update your payment method — until then the account is read-only.',
    inactiveNotice: 'Your subscription is not active, so the account is read-only. Renew it to keep editing.',
    card: 'Card ending in {{last4}}',
    usage: {
      connectedMailAccounts: 'Connected mailboxes',
      galleries: 'Galleries',
      quotesThisMonth: 'Quotes this month',
      voiceActionsThisMonth: 'Quotes dictated to the AI this month',
    },
    // The same three caps as `usage`, named as a cap rather than as a counter:
    // the account screen says how many quotes went out this month, the plan
    // picker says how many a month the plan allows.
    limitLabel: {
      connectedMailAccounts: 'Connected mailboxes',
      galleries: 'Galleries',
      quotesPerMonth: 'Quotes per month',
      voiceActionsPerMonth: 'Quotes dictated to the AI per month',
    },
    unlimited: 'Unlimited',
    notIncluded: 'Not included',
    usageOf: '{{used}} of {{limit}}',
    usageLeft: '{{left}} left',
    cancel: 'Cancel subscription',
    cancelConfirm: 'Cancel the subscription? Access stays until the end of the paid period, after which the account becomes read-only.',
    cancelDone: 'Subscription cancelled',
    checkout: 'Continue to payment',
    checkoutFailed: 'We could not open the payment page. Try again.',
    paymentSuccess: 'Payment received and your plan is updated',
    paymentCancelled: 'Payment cancelled — you were not charged',
    plans: {
      starter: 'Starter',
      business: 'Business',
      pro: 'Pro',
    },
    planTagline: {
      starter: 'For a one-person business running quotes and customers alone',
      business: 'For a steady flow of quotes: dictate the job to the AI, and it drafts a written quote that reaches the customer only after you approve it',
      pro: 'For a business that prepares most quotes by dictating to the AI, with the most dictations a month',
    },
  },
  dashboard: {
    finances: {
      incomeExpenses: 'Estimated income and expenses',
      year: 'Year',
      chartHeight: 'Chart height',
      annualTotals: 'Year totals',
      expectedRevenue: 'Expected revenue from approved quotes', thisMonth: 'This month',
      approvedCount: '{{count}} approved quotes', sixMonths: 'Last six months',
      archivedInvoices: 'This month’s invoices', archiveHint: 'Detected archive invoice totals this month in {{currency}}',
      income: 'Income', expenses: 'Expenses', unavailable: 'Financial totals could not be loaded',
    },
    greeting: {
      morning: 'Good morning, {{name}}',
      noon: 'Good afternoon, {{name}}',
      evening: 'Good evening, {{name}}',
      night: 'Good night, {{name}}',
    },
    range: {
      today: 'Today',
      week: 'This week',
      month: 'This month',
      all: 'All time',
    },
    kpi: {
      quotesSent: 'Quotes sent',
      quotesApproved: 'Quotes approved',
      incomingCalls: 'Incoming calls',
      // Sub-labels inside a stat card, where the card heading already says what
      // is being counted.
      sentShort: 'sent',
      pendingShort: '{{count}} awaiting',
      rejectedShort: '{{count}} rejected',
      missedShort: '{{count}} missed',
      callsTotal: 'calls in total',
    },
    conversion: '{{rate}}% conversion',
    meetings: 'Meetings booked',
    meetingsEmpty: 'Nothing booked for today',
    // The month-on-month line under the count. A first month has nothing to
    // compare against, so it says how many rather than inventing a percentage.
    vsLastMonth: '{{percent}}% vs last month',
    meetingsThisMonth: '{{count}} this month',
    overdueCount: '{{count}} overdue',
    allReminders: 'All reminders',
    moreReminders: '{{count}} more reminders',
    stats: 'Statistics',
    openReminders: 'Reminders & meetings',
    noReminders: 'No open reminders',
    quickActions: 'Quick actions',
    newQuote: 'New quote',
    newLead: 'New lead',
    newReminder: 'New reminder',
    newReminderMeeting: 'New reminder or meeting',
  },
  calls: {
    title: 'Calls', subtitle: 'Incoming and outgoing call history', caller: 'Caller', unknown: 'Unknown caller',
    startedAt: 'Date and time', duration: 'Duration', status: 'Status', direction: 'Direction', inbound: 'Incoming',
    outbound: 'Outgoing', redial: 'Call back', notes: 'Call notes',
    searchPlaceholder: 'Search by caller or number…',
    customer: 'Customer', openCustomer: 'Customer card', unmatched: 'This call is not linked to a customer',
    deleteConfirm: 'Delete this call record? This cannot be undone.',
    emptyTitle: 'No calls yet', emptyBody: 'Calls to your virtual number will appear here automatically.',
    emptyNoNumber: 'No virtual number is set yet. Add one under My profile so calls are logged here.',
  },
  miniSite: {
    subtitle: 'Your public showcase and inquiry channel', viewSite: 'View public site',
    slug: 'Public address', cover: 'Cover image', about: 'About the business',
    slugEmpty: 'The public site is offline until you pick an address and save. For example: {{example}}',
    areas: { north: 'North', haifa: 'Haifa & Krayot', sharon: 'Sharon', center: 'Center', jerusalem: 'Jerusalem', shfela: 'Shfela', south: 'South', eilat: 'Eilat' },
    serviceArea: 'Service area', specialties: 'Specialties',
    yearsActive_one: '1 year active', yearsActive_two: '2 years active', yearsActive_other: '{{count}} years active',
    projectsDone_one: '1 project', projectsDone_two: '2 projects', projectsDone_other: '{{count}} projects',
    projects: 'Projects', newRating: 'New rating',
    ratedByOf_one: 'from 1 rater', ratedByOf_two: 'from 2 raters', ratedByOf_other: 'from {{count}} raters',
    showAllAlbums: 'Show all ({{count}})', city: 'Town', whatsapp: 'WhatsApp', like: 'Like', call: 'Call', shareGallery: 'Share gallery',
    slogan: 'Slogan', foundedYear: 'Year founded', projectsCount: 'Number of projects',
    coverMenu: 'Image options', removeCover: 'Remove cover', removeLogo: 'Remove profile picture',
    removeCoverConfirm: 'Remove the cover image?', removeLogoConfirm: 'Remove the profile picture?',
    editAbout: 'Edit about',
    tabs: { gallery: 'Gallery', promotions: 'Promotions', reviews: 'Reviews', about: 'About' },
    share: 'Share', linkCopied: 'Site link copied',
    slugRequired: 'Pick a public address and save before sharing',
    changeCover: 'Change cover', changeLogo: 'Change logo', editName: 'Edit business name',
    galleries: 'Galleries', newGallery: 'New gallery', createGallery: 'Add gallery',
    editGallery: 'Edit gallery', noGalleries: 'No galleries yet',
    noGalleriesHint: 'A gallery is one job of yours: photos, a description and a price. It is the first thing a customer sees.',
    noSearchResults: 'No gallery matches that search',
    searchGalleries: 'Search galleries',
    orderGalleries: 'Gallery order',
    orderHint: 'Drag a gallery up or down — this is the order a customer sees on the site',
    orderSaved: 'The order was saved',
    viewMode: 'View', viewGrid: 'Grid', viewList: 'List',
    images: 'images', views: 'views', addImage: 'Add image', uploadImages: 'Upload photos',
    setCover: 'Set as cover', galleryName: 'Gallery name', openImage: 'Open image',
    imagesCount: '{{count}} photos', viewsCount: '{{count}} views', unitsLeft: '{{count}} left',
    noImages: 'No photos in this gallery', noDescription: 'No description written',
    description: 'Description', price: 'Price', quantity: 'Available quantity', priceFrom: 'Show “starting from”',
    published: 'Published', hidden: 'Hidden', onSale: 'Show as a promotion',
    badgeNew: 'NEW', badgeSale: 'SALE',
    deleteGallery: 'Permanently delete this gallery and all its images?',
    deleteGalleries: 'Permanently delete {{count}} galleries and all their images?',
    clearSelection: 'Clear selection',
    albumMenu: 'Gallery menu', deleteAlbum: 'Delete gallery', shareAlbum: 'Share gallery',
    albumLinkCopied: 'Gallery link copied',
    markSale: 'Mark as a promotion', unmarkSale: 'Remove from promotions',
    attachToQuote: 'Attach to a quote',
    album: 'Album',
    albumTabs: { description: 'Project description', plans: 'Plans' },
    noPlans: 'No plans attached', plansHint: 'Attaching drawings and plans is coming later.',
    reviews: 'Reviews', noReviews: 'No reviews yet',
    deleteImage: 'Permanently delete this image from the gallery?', deleteReview: 'Permanently delete this review?',
    newReview: 'New review', editReview: 'Edit review', addReview: 'Add review',
    reviewBody: 'Review', showPhone: 'Show phone publicly',
    rating: { service: 'Service', timeliness: 'On time', value: 'Value for money' },
    ratingPreview: 'Review average', ratingBreakdown: 'Rating breakdown', ratingOverall: 'Overall',
    ratingOf: '{{rating}} out of 5', ratedBy: '{{count}} ratings',
    offers: 'Offers', newOffer: 'New offer', createOffer: 'Add offer', editOffer: 'Edit offer',
    noOffers: 'No offers yet',
    noOffersHint: 'An offer is a price with an end date. It leaves the site by itself when the time is up.',
    offerName: 'Offer name', validDays: 'Valid for (days)',
    validDaysHint: 'The offer leaves the site by itself when the time is up',
    deleteOffer: 'Permanently delete this offer?',
    offerActive: 'Live', offerInactive: 'Ended',
    fromAlbum: 'Gallery marked as a promotion',
    daysLeft: '{{count}} days left', lastDay: 'Last day', expired: 'Expired',
    businessDescription: 'About the business', fields: 'Trades',
    newField: 'New trade', removeField: 'Remove trade',
    public: {
      notFound: 'This site was not found',
      contact: 'Contact us',
      contactTitle: "We'd love to get back to you",
      getDetails: 'Request details',
      priceFrom: 'From ',
      message: 'How can we help?',
      callNow: 'Call now',
      whatsapp: 'WhatsApp',
      builtWith: 'Built with Worki',
      galleries: 'galleries',
      offers: 'Offers',
      reviews: 'reviews',
      rating: 'average rating',
      ratedBy: 'from {{count}} reviews',
      consentLabel: 'I agree to receive offers and updates from this business (optional)',
      consentNote: 'We will get back to you either way, and you can withdraw this consent at any time.',
      privacyNotice:
        'The details you enter — name, phone, email and your message — are optional, are held only by the business owner, and are used only to get back to you.',
      submit: 'Send enquiry',
      sent: 'Your message was sent. We will get back to you shortly.',
      sendFailed: 'We could not send your message. Check the details and try again.',
      gallery: 'Gallery',
      emptyGalleries: 'No gallery photos yet',
    },
  },
  inquiries: {
    one: 'Inquiry',
    new: 'New inquiry',
    subject: 'Subject',
    message: 'Message',
    status: 'Status',
    source: 'Source',
    createdAt: 'Created',
    // One label because it is one field now: the day and the hour are set
    // together, in the picker the phone already opens for both.
    meetingAt: 'Meeting time',
    convertToLead: 'Convert to lead',
    converted: 'Inquiry converted to a lead',
    deleteConfirm: 'Delete this inquiry? This cannot be undone.',
    bulkStatus: 'Change status',
    searchPlaceholder: 'Search by name, phone or subject',
    reminderAt: 'Reminder — when',
    reminderCreated: 'Reminder created',
    reminderHint: 'A reminder made here also appears on the reminders screen.',
    tabs: { inquiries: 'Inquiries', agents: 'Agents (coming soon)' },
    agentsBody:
      'Coming soon: an agent will answer your virtual number, ask the questions you set and open an inquiry from the answers. You can set it up already.',
    agentsCount: 'Agents ({{count}})',
    agentNew: 'New agent',
    agentEdit: 'Edit agent',
    agentName: 'Agent name',
    agentNamePlaceholder: 'For example: Amit',
    agentVoice: 'Voice',
    agentNumber: 'Virtual phone number',
    agentNumberPick: 'Pick a number…',
    agentNoNumbers: 'This business holds no virtual number yet. Assign one from the personal area, then attach it to an agent.',
    agentForwardTo: 'Hand the call over to',
    agentForwardToPlaceholder: 'Your phone number',
    agentTrade: 'Trade',
    agentTradePlaceholder: 'For example: renovation and construction',
    agentSpecialties: 'Specialities',
    agentSpecialtiesPlaceholder: 'For example: kitchens, bathrooms',
    agentInbound: 'Questions on an inbound call (AI)',
    agentOutbound: 'Questions on an outbound call (AI)',
    agentQuestionsPlaceholder: 'One question per line',
    agentActive: 'Agent active',
    agentNoTrades: 'No trades set',
    agentNoNumber: 'No number attached',
    agentDeleteConfirm: 'Delete this agent? Calls it already answered are kept, and its number is freed for another agent.',
    agentsEmpty: 'No agent set up yet',
    agentsEmptyBody: 'Coming soon: an agent will answer the business number for you, ask what you need to know and open an inquiry from the answers.',
    agentSaved: 'Agent saved',
    emptyTitle: 'No inquiries yet',
    emptyBody: 'Inquiries arrive automatically from calls and the mini-site, or add one manually.',
  },
  customer: {
    addContactValue: 'Add another',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    city: 'City',
    match: {
      body: 'The number {{phone}} is already on the card of "{{name}}".',
      update: 'Update to "{{name}}"',
      keep: 'Continue with "{{name}}"',
      new: 'Separate card',
      updateHint:
        'The details you typed replace the ones on the card. The name will also appear on quotes and documents already issued to this customer.',
      keepHint: 'The details you typed are not saved, and the document goes out under "{{name}}".',
      newHint: 'A second card with the same number. An incoming call from it stays on the first card.',
      required: 'Choose what to do with this number before saving.',
    },
  },

  quotes: {
    saveChanges: 'Save changes',
    customFollowUpHours: 'Custom follow-up (1–720 hours)',
    projectLocation: 'Project location',

    revisionChanged: 'This quote has changed since you opened it. Loading the latest version will replace your unsaved changes.',
    reloadLatest: 'Load latest version',
    attachAlbums: 'Attach albums',
    selectAlbums: 'Select one or more albums',
    attachedAlbums: 'Attached albums',
    additionalProjects: 'Additional projects by {{name}}',

    title: 'Quotes',
    one: 'Quote',
    number: 'Quote number',
    numberShort: 'Quote #{{number}}',
    numberBadge: 'Quote {{number}}',
    new: 'New quote',
    edit: 'Edit quote',
    searchPlaceholder: 'Search by name, phone or quote number',
    quoteTitle: 'Quote title',
    customer: 'Customer',
    items: 'Line items',
    addItem: 'Add item',
    description: 'Description',
    quantity: 'Qty',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    included: 'Included',
    subtotal: 'Subtotal',
    discount: 'Discount',
    discountType: 'Discount type',
    discountNone: 'No discount',
    discountAmount: 'Fixed amount',
    discountPercent: 'Percentage',
    discountLabel: 'Discount label',
    discountTemplates: 'Discount templates',
    net: 'Net (before VAT)',
    vat: 'VAT {{rate}}%',
    vatOfWhich: 'Of which VAT {{rate}}%',
    vatIncluded: 'Prices include VAT',
    total: 'Total',
    notes: 'Notes for the customer',
    legalText: 'Terms & agreement',
    legalTemplates: 'Saved templates',
    insertTemplate: 'Insert template',
    saveAsTemplate: 'Save as template',
    templateName: 'Template name',
    deleteTemplate: 'Delete the template "{{name}}"',
    deleteTemplateConfirm: 'Delete the template "{{name}}"? This cannot be undone.',
    saveDraft: 'Save as draft',
    sendQuote: 'Send quote',
    sendVia: 'Send via',
    email: 'Email',
    whatsapp: 'WhatsApp',
    downloadPdf: 'Download PDF',
    copyLink: 'Copy link',
    openPublicLink: 'Open customer link',
    sentAt: 'Sent on',
    approvedAt: 'Approved on',
    rejectedAt: 'Rejected on',
    // 1.20 — how long the quote is good for, in days and never as a date.
    validFor_one: 'Quote valid for 1 day',
    validFor: 'Quote valid for {{count}} days',
    createReminder: 'Create reminder',
    tracking: 'View tracking',
    viewCount: 'Views',
    firstViewed: 'First viewed',
    lastViewed: 'Last viewed',
    viewDuration: 'Total time viewed',
    notViewedYet: 'The customer has not opened this quote yet',
    notViewedShort: 'Not opened',
    viewedFor: 'Viewed {{duration}}',
    rejectionReason: 'Rejection reason',
    rangeLabel: 'Period',
    viewDocument: 'View document',
    duplicateForAnotherCustomer: 'Duplicate quote for another customer',
    fullDetails: 'Full details',
    sendAgain: 'Send again',
    draftDocument: 'Draft quote',
    toHonour: 'To',
    docDate: 'Date: {{date}}',
    serviceDescription: 'Service description',
    servicePlaceholder: 'Describe the service you are offering…',
    serviceTemplates: 'Description templates',
    notesPlaceholder: 'Anything the customer should know…',
    legalPlaceholder: 'Payment terms, terms of service, agreement…',
    discountValue: 'Discount amount',
    preview: 'Preview',
    noTemplates: 'No saved templates',
    noItemsYet: 'No items added',
    noVat: 'No VAT',
    totalWithVat: 'Total payable (incl. VAT)',
    totalNoVat: 'Total payable (excl. VAT)',
    producedBy: 'This document was produced with Worki',
    pricesIncludeVat: 'VAT included',
    pricesPlusVat: 'VAT on top',
    sendChannels: 'Send channels',
    sendOptions: 'Send channels and settings',
    validDays: 'Quote valid for',
    validDaysOption_one: '1 day',
    validDaysOption: '{{count}} days',
    validDaysDefault: 'Default ({{count}} days)',
    vatOnQuote: 'VAT on this quote',
    vatRateLabel: 'VAT rate',
    templateSaved: 'Saved as a template',
    templateBody: 'Template text',
    editTemplateTitle: 'Edit template',
    editTemplate: 'Edit the template {{name}}',
    vatRateHint: 'Defaults to the platform rate. Change it only when this quote is taxed differently.',
    vatRateReset: 'Back to {{rate}}%',
    followUp: 'Follow-up',
    hoursShort: '{{count}}h',
    followUpDefault: "The business's own threshold",
    followUpEvery_one: 'Chase it after an hour with no answer',
    followUpEvery: 'Chase it after {{count}} hours with no answer',
    pickAChannel: 'Choose at least one send channel',
    sendHistory: 'Send history',
    sendMenu: 'Send options',
    sendSettings: 'Send settings',
    sendSettingsSaved: 'The setting was saved',
    noSends: 'This quote has not been sent yet',
    sentTo: 'Sent to {{to}}',
    notesTerms: 'Notes / terms of service',
    itemTemplates: 'Item templates',
    removeItems: 'Delete items — press and hold to choose',
    removeSelectedItems: 'Delete the chosen items',
    doneSelecting: 'Done choosing',
    dictate: 'Dictate',
    dictateStop: 'Stop dictating',
    tab: {
      all: 'All',
      pending: 'Sent/pending',
      viewed: 'Viewed',
      expired: 'Expired',
      rejected: 'Rejected',
      approved: 'Signed / approved',
      locked: 'Locked',
      draft: 'Drafts',
    },
    deleteConfirm: 'Delete this quote? This cannot be undone.',
    deleteSomeRefused_one:
      'One quote was not deleted: an approved quote is kept as the record of the agreement.',
    deleteSomeRefused: '{{count}} items could not be removed. Refresh the list and try again.',
    approvedNotDeletable: 'An approved quote is kept as a record and cannot be deleted',
    cannotEditSent: 'A quote that has been sent cannot be edited',
    emptyTitle: 'No quotes yet',
    emptyBody: 'Create your first quote and send it to a customer in one click.',
    sentSuccess: 'Quote sent to the customer',
    whatsappReady: 'Link ready — tap to open WhatsApp',
  },
  publicQuote: {
    revisionChanged: 'This quote was updated. Load and review the latest version before approval.',
    reloadLatest: 'Load latest quote',
    from: 'Quote from {{business}}',
    to: 'To {{name}}',
    validUntil: 'Valid until {{date}}',
    approve: 'Approve quote',
    reject: 'Reject quote',
    approveTitle: 'Approve quote',
    approveIntro: 'To complete the approval, enter the following code:',
    yourCode: 'Your code',
    codeHint: 'Type the code below to approve this quote.',
    enterCode: 'Enter approval code',
    confirmApprove: 'Confirm approval',
    wrongCode: 'That code is incorrect. Please try again.',
    codeExpired: 'The code has expired. Start the approval again.',
    approved: 'Quote approved',
    approvedBody: 'A signed copy has been sent to you and to the business owner. Thank you!',
    rejectTitle: 'Reject quote',
    rejectIntro: 'Help us understand why:',
    rejectNote: 'Additional note',
    confirmReject: 'Submit rejection',
    rejected: 'Rejection recorded',
    rejectedBody: 'Thank you for the feedback. The business owner has been notified.',
    expiredTitle: 'This quote has expired',
    expiredBody_one: 'The link was valid for 1 day. Contact the business owner for an updated quote.',
    expiredBody: 'The link was valid for {{count}} days. Contact the business owner for an updated quote.',
    notFound: 'Quote not found',
    generatedBy: 'Generated by Worki',
  },
  reminders: {
    openSource: 'Open source document',
    selectAllMatching: 'Select all reminders ({{count}})',
    bulkComplete: 'Mark completed',
    bulkDeleteConfirm: 'Delete {{count}} selected reminders?',
    mode: 'What would you like to create?', reminderTab: 'Reminder', meetingTab: 'Meeting', newMeeting: 'New meeting',
    meetingTitle: 'Meeting subject', meetingCreationIncomplete: 'The inquiry was created, but scheduling failed. Retry to complete the meeting.',
    new: 'New reminder',
    edit: 'Edit reminder',
    reminderTitle: 'Title',
    notes: 'Details',
    dueAt: 'Due',
    status: 'Status',
    markComplete: 'Mark complete',
    markOpen: 'Reopen',
    open: 'Open',
    overdue: 'Overdue',
    snoozeDay: 'Snooze 1 day',
    snoozedUntil: 'Next alert: {{date}}',
    fromDate: 'From date',
    toDate: 'To date',
    emptyTitle: 'No reminders',
    emptyBody: 'Create a reminder so you never miss a follow-up.',
    deleteConfirm: 'Delete this reminder?',
    fromQuote: 'From quote #{{number}}',
    fromInquiry: 'From inquiry',
    fromCustomer: 'From customer card',
    searchPlaceholder: 'Search reminders and meetings…',
    previewOpen: 'Still open and waiting to be done',
    previewDone: 'Marked as done',
    feed: {
      all: 'Everything',
      notification: 'Notifications',
      reminder: 'Reminders',
      meeting: 'Meetings',
      activity: 'Activity',
    },
  },
  activity: {
    subtitle: 'Every action is recorded and cannot be altered',
    actor: 'Actor',
    action: 'Action',
    entity: 'Record',
    when: 'When',
    empty: 'No recorded activity',
    viewAll: 'View all activity',
  },
  reports: {
    title: 'Reports & statistics',
    subtitle: 'Performance by period, across every module',
    domains: {
      quotes: 'Quotes',
      inquiries: 'Inquiries',
      reminders: 'Reminders',
      calls: 'Calls',
      invoices: 'Invoices',
      operations: 'Operations',
      revenue: 'Revenue',
    },
    revenueApproved: 'Revenue this period',
    revenueAvgDeal: 'Average deal',
    revenueOpen: 'Open deal value',
    revenueGrowth: 'Growth vs. previous period',
    csv: {
      number: 'Number',
      customer: 'Customer',
      quoteTitle: 'Title',
      status: 'Status',
      currency: 'Currency',
      subtotal: 'Subtotal',
      discount: 'Discount',
      net: 'Net',
      vatRate: 'VAT rate',
      vat: 'VAT',
      total: 'Total',
      totalFormatted: 'Total (formatted)',
      createdAt: 'Created',
      sentAt: 'Sent',
      approvedAt: 'Approved',
      rejectedAt: 'Rejected',
      rejectionReason: 'Rejection reason',
      viewCount: 'View count',
    },
    conversionRate: 'Conversion rate',
    avgResponseTime: 'Average response time',
    viewRate: 'View rate',
    avgViewCount: 'Avg. views per quote',
    avgViewDuration: 'Average view duration',
    responseMinutes_one: '1 minute',
    responseMinutes: '{{count}} minutes',
    responseHours_one: '1 hour',
    responseHours: '{{count}} hours',
    responseDays_one: '1 day',
    responseDays: '{{count}} days',
    reconcileNote: "These numbers match the dashboard's KPI cards for the same period.",
    comparison: 'Comparison to previous period',
    noComparison: 'No comparison available for "all time"',
    chart: 'Trend over time',
    quotesCreated: 'Quotes created',
    quotesSent: 'Quotes sent',
    quotesApproved: 'Quotes approved',
    quotesRejected: 'Quotes rejected',
    inquiriesCreated: 'Inquiries received',
    metric: 'Metric',
    totalInquiries: 'Total inquiries',
    totalCalls: 'Total calls',
    inquiriesMeetings: 'Led to a meeting',
    inquiriesQuoteRequested: 'Asked for a quote',
    closeRate: 'Close rate',
    openReminders: 'Still open',
    exportCsv: 'Export CSV',
    noData: 'No data yet for the selected period',
    vsLastPeriod: 'vs. previous period',
  },
  invoices: {
    forwarding: {
      title: 'Automatic invoice forwarding', addRule: 'Add automatic forwarding', empty: 'No automatic forwarding configured',
      recipientEmail: 'Recipient email', fromDay: 'From date', toDay: 'To date',
      dateHint: 'A fixed inclusive date range. Only invoices received by email are forwarded.',
      delivery: 'Delivery', pdf: 'PDF file', link: 'Link', folder: 'Folder', account: 'Email account',
      retryUnsent: 'Allow another attempt for unsent invoices',
      retryHint: 'Invoices that failed before sending or were skipped return to the preview. This does not send mail. Deliveries with unknown acceptance remain blocked.',
      skippedCount: '{{count}} invoices skipped',
      allFolders: 'All folders', allAccounts: 'All email accounts', intervalMinutes: 'Check interval in minutes',
      savePreview: 'Save paused and review', previewTitle: 'Review forwarding settings',
      sentCount: '{{count}} invoices sent', failedCount: '{{count}} invoices failed', uncertainCount: '{{count}} invoices need delivery review',
      matchedCount: '{{count}} invoices in range', pendingCount: '{{count}} new invoices to forward', blockedCount: '{{count}} invoices need review',
      enableHint: 'Enabling automatically forwards newly discovered invoices in this fixed range. Editing does not replay handled invoices. Create a new rule to deliver them to another recipient.',
      enable: 'Enable automatic forwarding', pause: 'Pause', enabled: 'Active', paused: 'Paused', edit: 'Edit settings', delete: 'Delete settings',
      runNow: 'Send new invoices now', runs: 'Forwarding history', noRuns: 'Nothing forwarded yet',
      pending: 'Pending', running: 'In progress', completed: 'Sent', failed: 'Failed', uncertain: 'Check delivery',
      uncertainHint: 'The email may have been delivered. Check with the recipient before manually sending again to avoid duplicates.',
      previewRequired: 'Review the preview before enabling forwarding', lastRun: 'Last check', missingTotal: 'Some invoices have no detected amount',
    },
    removeFromFolder: 'Remove from folder',
    removeFromFolderConfirm: 'Remove the selected invoices from this folder? The originals will remain in the archive.',
    connectAccount: 'Connect email account',
    status: 'Status',
    lastSync: 'Last sync',
    disconnect: 'Disconnect',
    disconnectConfirm:
      'Disconnect {{email}}? The {{count}} invoices scanned from this mailbox will be deleted along with their files, and cannot be restored.',
    disconnectConfirmCounting:
      'Disconnect {{email}}? The invoices scanned from this mailbox will be deleted along with their files, and cannot be restored.',
    maxAccountsReached: 'You have reached the limit of 2 email accounts. Disconnect one to connect another.',
    accountConnected: 'Account connected successfully',
    statusActive: 'Active',
    statusError: 'Sync error',
    statusReconnect: 'Login needed',
    reconnect: 'Log in again',
    reconnectTitle: 'Log in again to {{email}}',
    reconnectHint:
      'The connection to this mailbox expired, so syncing stopped. Once you log in, syncing picks up where it left off. Your invoices and folders stay where they are, and there is no need to disconnect the mailbox.',
    reconnectWithGoogle: 'Log in with Google',
    reconnectWithMicrosoft: 'Log in with Microsoft',
    reconnectPasswordHint: 'Enter the mailbox\'s app password. If the old one was revoked, create a new one with your mail provider.',
    accountReconnected: 'The mailbox is reconnected, and syncing continues',
    disconnectInsteadOfReconnect: 'To renew the connection without deleting anything, close this and choose "Log in again".',
    oauthWrongMailbox: 'A different mailbox was chosen on the login screen. Try again and pick the same mailbox.',
    number: 'Number',
    issuedAt: 'Issued',
    receivedAt: 'Received by email',
    addedAt: 'Added to Worki',
    folderFiltered: 'Showing {{visible}} of {{count}} invoices in this folder. Other filters are active.',
    showAllInFolder: 'Show all invoices in this folder',
    fromAccount: 'Received in account',
    source: 'Document source',
    sourceAttachment: 'Attached to the mail',
    fromLink: 'Fetched from a link',
    sourceBadgeLink: 'Link',
    sourceBadgeFile: 'File',
    sourceBadgeManual: 'Added by hand',
    fromLinkNote: 'The mail carried a link, not a file. This is the copy Worki downloaded or printed from that page.',
    download: 'Download',
    emptyTitle: 'No invoices yet',
    emptyBody: 'Connect an email account and new invoices will be picked up automatically.',
    deleteConfirm: 'Delete this invoice? This cannot be undone.',
    deleteSelectedConfirm_one: 'Delete the selected invoice? This cannot be undone.',
    deleteSelectedConfirm: 'Delete {{count}} selected invoices? This cannot be undone.',
    deleteSomeRefused_one: 'One invoice was not deleted.',
    deleteSomeRefused: '{{count}} invoices were not deleted.',
    allAccounts: 'All accounts',
    syncNow: 'Sync now',
    resetAll: 'Delete all and rescan',
    resetAllConfirm:
      'Every invoice in the list will be deleted along with its stored file, and a fresh scan of the past four years will start on every connected mailbox. An invoice whose mail has left that window will not come back. This cannot be undone.',
    resetAllAction: 'Delete and rescan',
    resetAllDone_one: 'One invoice deleted, the scan has started',
    resetAllDone: '{{count}} invoices deleted, the scan has started',
    connect: 'Connect',
    connectHint: 'Connect up to {{max}} accounts to sync',
    searchPlaceholder: 'Search by supplier, email or amount…',
    count_one: '1 invoice',
    count: '{{count}} invoices',
    detailTitle: 'Invoice details',
    synced_one: '1 new invoice synced',
    synced: '{{count}} new invoices synced',
    demoProviderNote: 'Dev mode: any email address connects through a demo provider (set INVOICE_SYNC_DRIVER=imap for a real mailbox).',
    notSyncedYet: 'Not synced yet',
    mailProvider: 'Mail provider',
    customImap: 'Other (custom IMAP)',
    imapHost: 'IMAP server',
    imapPort: 'Port',
    appPassword: 'App password',
    appPasswordHint:
      'Use an app-specific password, not your account password. Gmail: Google Account → Security → App passwords. Outlook: Security → Advanced security options.',
    imapCredentialsRequired: 'IMAP server and app password are required',
    imapAppPasswordRequired:
      'Google requires an app password, not your account password. Create one under Google → Security → App passwords and paste it here.',
    imapAuthFailed: 'The email or app password is incorrect. Try again.',
    imapConnectFailed: 'Could not reach the mailbox. Check the IMAP server address and port.',
    connectWithGoogle: 'Connect with Google',
    connectWithMicrosoft: 'Connect with Microsoft',
    oauthHint: 'Approve it on your provider\'s own screen — no app password. Worki gets read-only access, and you can withdraw it at any time from your account with the provider.',
    orAppPassword: 'or connect with an app password',
    oauthFailed: 'The mailbox connection failed. Try again.',
    oauthCancelled: 'The connection was cancelled, and no mailbox was added.',
    oauthDisabled: 'One-click connection is not available on this server.',
    oauthReconnectRequired: 'The mailbox permission expired or was withdrawn. Log in again to keep syncing.',
    oauthNoOfflineAccess:
      'The permission was granted without ongoing access, so syncing would stop within the hour. Try again and approve everything the provider asks for.',
    oauthConnectOnly: 'A mailbox like this one connects on the provider\'s approval screen, not on this form.',
    rangeLabel: 'Period',
    range: {
      week: 'This week',
      month: 'This month',
      twoMonths: 'Last two months',
      year: 'This year',
      all: 'All time',
    },
    period: {
      custom: 'Or pick dates',
      fromDay: 'From {{day}}',
      untilDay: 'Until {{day}}',
      yearsCount: '{{count}} years',
      fromLabel: 'From',
      toLabel: 'To',
      clearCustom: 'Clear the dates',
    },
    share: 'Share',
    folders: 'Folders',
    noFolder: 'No folder',
    noFoldersYet: 'No folders yet',
    manageFolders: 'Manage folders',
    newFolder: 'New folder',
    createFolderAction: 'Create a new folder',
    folderName: 'Folder name',
    moveToFolder: 'Move to folder',
    moveToNewFolder: 'New folder…',
    createAndMove: 'Create and move',
    moved_one: 'One invoice was moved',
    moved: '{{count}} invoices were moved',
    deleteFolder: 'Delete folder',
    deleteFolderConfirm: 'The invoices in it are not deleted — they stay in the invoice list, without a folder.',
    deleteFolderNamed_zero: 'Delete the folder "{{name}}"? There are no invoices in it.',
    deleteFolderNamed_one: 'Delete the folder "{{name}}"? The invoice in it is not deleted — it stays in the invoice list, without a folder.',
    deleteFolderNamed: 'Delete the folder "{{name}}"? The {{count}} invoices in it are not deleted — they stay in the invoice list, without a folder.',
    emptyFolder: 'Empty the folder',
    emptyFolderAction: 'Empty',
    emptyFolderConfirm_one: 'The invoice in it is taken out and stays in the invoice list, without a folder. The folder itself stays, and nothing is deleted.',
    emptyFolderConfirm: 'All {{count}} invoices in it are taken out and stay in the invoice list, without a folder. The folder itself stays, and nothing is deleted.',
    emptied_one: 'One invoice was taken out of the folder',
    emptied: '{{count}} invoices were taken out of the folder',
    selectAllMatching: 'Select all {{count}} results',
    selectPageOnly: 'Select this page only ({{count}})',
    selectionCapped: 'One action can take up to {{count}} invoices. The first of them are selected — narrow the filter and send it in two batches.',
    shareTitle: 'Share invoices',
    shareHint: 'The selected invoices are merged into a single PDF.',
    sharePartial: 'Some channels failed. Successful channels were recorded. Check delivery before retrying the remaining channels.',
    recipientLanguage: 'Recipient language',
    shareFileTitle: 'File title',
    shareFileTitleHint: 'For example: September 2026 invoices',
    shareFolderTitle: 'Share folder — {{name}}',
    shareFolderAction: 'Share this folder',
    folderSentAt: 'Sent {{date}} {{time}}',
    renameFolderAction: 'Rename this folder',
    shareInvoiceTitle: 'Share invoice — {{name}}',
    shareRowAction: 'Send this invoice',
    shareFolders_one: 'Share folder',
    shareFolders: 'Share {{count}} folders',
    sharePhoneHint: 'Leave blank to pick the contact in WhatsApp',
    shareSent: 'The invoices were sent',
    shareSentLink: 'The file is too large to attach to an email — a download link was sent instead',
    shareReady: 'WhatsApp opened with a link to the file',
    shareSkipped_one: 'One invoice is not in the file — no readable document was found for it',
    shareSkipped: '{{count}} invoices are not in the file — no readable document was found for them',
    // 1.9 — the mail carries the file or a link to it, chosen in the dialog before it goes.
    shareDelivery: 'Send as',
    shareAsPdf: 'PDF',
    shareAsLink: 'Link',
    // Under the choice while the PDF is greyed out: the merged file is over what a
    // mail can carry, so the link is what goes.
    sharePdfTooBig: 'The files are too large to attach to an email',
    pickMonth: 'Month',
    pickYear: 'Year',
    allYears: 'All',
    anyMonth: 'Whole year',
    totalSpent: 'Invoice total',
    summarySelected_one: 'Total for the one selected invoice',
    summarySelected: 'Total for the {{count}} selected invoices',
    vatCredit: 'VAT to reclaim',
    vatCreditHint: 'at {{rate}}% VAT',
    vatCreditFrom_one: 'from one tax invoice of {{total}}',
    vatCreditFrom: 'from {{count}} tax invoices of {{total}}',
    vatCreditInfo:
      'An estimate of the VAT you can deduct, at the current VAT rate. It is worked out only from tax invoices in shekels — receipts, transaction invoices and income are not counted. The final figure is settled with your accountant.',
    foldersInfo:
      'A folder groups invoices, for example by month or for your accountant. An invoice moved into a folder also stays in the regular invoice list.',
    shareFolderInfo:
      'All the invoices in the folder go out together, as one PDF or a link to it, to whoever you choose. Once sent, the folder shows when it went.',
    vatDisclaimer: '* The VAT figure is for illustration only and is not a substitute for professional advice',
    missingAllocation_one:
      'One tax invoice needed an allocation number for its date and carries none — the Tax Authority will not allow its VAT to be deducted.',
    missingAllocation:
      '{{count}} tax invoices needed an allocation number for their date and carry none — the Tax Authority will not allow their VAT to be deducted.',
    docType: {
      tax_invoice: 'Tax invoice',
      tax_invoice_receipt: 'Tax invoice / receipt',
      en_tax_invoice: 'Tax invoice in English',
      receipt: 'Receipt',
      proforma: 'Payment demand',
      credit_note: 'Credit note',
      not_yet_tax_invoice: 'Not yet a tax invoice',
      en_invoice: 'Not a tax invoice',
      other: 'Type not recognised',
    },
    docTypeFilter: 'Document type',
    docTypeFilterAll: 'All document types',
    docTypeSelected_one: 'One type',
    docTypeSelected: '{{count}} types',
    // 1.5 — the five choices the filter offers, in the order Nitzan listed them.
    // They name what a person looks for, not the types behind them.
    docTypeOption: {
      taxInvoiceReceipt: 'Tax invoice / receipt',
      receipt: 'Receipt',
      creditNote: 'Credit note',
      english: 'Invoice in English',
      unrecognised: 'Not recognised',
    },
    fromInvoiceCount: 'from {{count}} invoices',
    invoiceCount_one: 'One invoice',
    invoiceCount: '{{count}} invoices',
    missingAmount_one: 'One invoice has no readable amount',
    missingAmount: '{{count}} invoices have no readable amount',
    missingAmountShow_one: 'Show it',
    missingAmountShow: 'Show them',
    missingAmountFilter: 'Without a readable amount',
    missingAmountEmpty: 'Every invoice in this period has an amount on it.',
    clearFilter: 'Clear',
    scanning: 'Scanning the mailbox',
    scanSearching: 'Going through the past four years of mail…',
    scanExamined: '{{examined}} messages checked…',
    scanProgress: '{{scanned}} of {{total}} messages',
    scanFound_one: '1 invoice found',
    scanFound: '{{count}} invoices found',
    elapsed: '{{seconds}}s',
    remaining: '{{time}} left',
    secondsShort_one: '1s',
    secondsShort: '{{count}}s',
    minutesShort_one: 'about a minute',
    minutesShort: 'about {{count}} min',
    syncedNone: 'No new invoices found',
    syncStalled: 'The scan stopped partway through. Run it again — whatever was already imported is kept.',
    syncRateLimited: 'Google limited how fast the mailbox can be read. Wait a few minutes and run the scan again — whatever was already imported is kept.',
    income: 'Income',
    customer: 'Customer',
    incomeTotal: 'Income',
    incomeVat: 'Output VAT',
    incomeShareLine: '{{count}} invoices · Income: {{total}} · Output VAT ({{rate}}%): {{vat}}',
  },
  profile: {
    resetColors: 'Original colors',
    saveSettings: 'Save changes',
    settingsSaved: 'Settings saved',
    invertColors: 'Invert colors',
    borderIntensity: 'Border intensity',
    planContents: 'Plan contents and remaining allowance',
    // An uncapped allowance still needs its counter: without one, the plan
    // whose whole selling point is "no ceiling" is the only plan that never
    // says how much has been used.
    planUsed: '{{used}} used',
    // The heading of the block that moved off the subscriber card.
    remindersAndCurrency: 'Reminders and currency',

    subscriber: 'Subscriber details',
    slogan: 'Slogan',
    quoteFooter: 'Quote footer text',
    virtualNumber: 'Virtual number',
    virtualNumberHint: 'The number your telephony provider routes to you. Inbound calls to it are logged on the Calls screen.',
    provisionNumber: 'Get a number',
    provisionNumberConfirm: 'Buy a new number from the phone provider? It carries a monthly charge on the provider account.',
    provisionNumberDone: 'The number was provisioned and assigned to you',
    deleteAccount: 'Delete account',
    deleteAccountBody:
      'Deletes your account and everything filed under it. If nobody else uses this business, the business goes too — customers, quotes, invoices, portfolio and files. There is no undo.',
    deleteAccountLearnMore: 'What exactly is deleted, and what is kept',
    deleteAccountWarning:
      'This cannot be undone. Once deleted, the data is gone and we cannot restore it — not from a backup, not on request.',
    deleteAccountConfirmLabel: 'Type {{email}} to confirm',
    deleteAccountConfirm: 'Delete permanently',
    deleteAccountDone: 'Your account has been deleted',
    taxId: 'Tax ID',
    currency: 'Currency',
    address: 'Address',
    quoteReminderHours: 'Follow-up reminder if no response (hours)',
    quoteReminderHoursValue_one: '1 hour',
    quoteReminderHoursValue: '{{count}} hours',
    quoteReminderHoursHint: 'If the customer has not responded within this many hours of the quote being sent, a follow-up reminder is created. The quote link is valid for 7 days, so the maximum is 168.',
    signatureReminderHours: 'Follow-up reminder on an unsigned document (hours)',
    signatureReminderHoursValue_one: '1 hour',
    signatureReminderHoursValue: '{{count}} hours',
    signatureReminderHoursHint: 'If the document has not been signed within this many hours of being sent, a follow-up reminder is created. A signing link is valid for 30 days, so the maximum is 720.',
    saved: 'Details saved',
    plan: 'My plan',
    settings: 'Settings',
    allDetails: 'All business details',
    editDone: 'Done',
    appearance: 'Display mode',
    listGradient: 'List gradient',
    listGradientHint: 'From 0 (flat rows) to 100. The tint follows the system colour picked above.',
    light: 'Light',
    dark: 'Dark',
    reports: 'Reports and statistics',
    reportsRange: 'This month',
    reportsOpen: 'Open the full reports',
    admin: 'Admin',
    adminBody: 'Worki platform management — businesses, subscriptions, plans and staff. Visible to Worki staff only.',
    adminOpen: 'Open the admin screen',
  },
  notifications: {
    title: 'Notifications',
    empty: 'No new notifications',
    markAllRead: 'Mark all as read',
    quoteOpened: '{{customer}} opened quote #{{number}}',
    quoteApproved: 'Quote #{{number}} was approved by {{customer}}',
    quoteRejected: 'Quote #{{number}} was rejected by {{customer}}',
    inquiryCreated: 'New inquiry from {{customer}}',
    reminderDue: 'Reminder due: {{title}}',
    openReminders: '{{count}} open reminders',
    openRemindersHint: 'Go to the reminders list',
  },
  enum: {
    callStatus: { answered: 'Answered', missed: 'Missed', callback: 'Callback' },
    agentVoice: {
      female: 'Female',
      male: 'Male',
    },
    inquiryStatus: {
      pending: 'Awaiting response',
      meeting_scheduled: 'Meeting booked',
      quote_requested: 'Quote requested',
      closed: 'Closed',
    },
    inquirySource: {
      manual: 'Manual entry',
      telephony: 'Phone call',
      mini_site: 'Mini-site',
      integration: 'Integration',
    },
    quoteStatus: {
      draft: 'Draft',
      sent: 'Sent/pending',
      approved: 'Approved',
      rejected: 'Rejected',
      expired: 'Expired',
    },
    rejectionReason: {
      price: 'Price is too high',
      better_offer: 'Received a better offer',
      improve_terms: 'Would like to improve the terms',
      not_relevant: 'No longer relevant',
    },
    reminderStatus: {
      open: 'Open',
      completed: 'Completed',
    },
    reminderSource: {
      manual: 'Manual',
      quote: 'Quote',
      inquiry: 'Inquiry',
      customer: 'Customer',
      call: 'Call',
    },
    entityType: {
      customer_payment: 'Customer payment',
      ai_agent: 'Phone agent',
      business: 'Business',
      call: 'Call',
      connected_mail_account: 'Mail account',
      customer: 'Customer',
      gallery: 'Gallery',
      gallery_image: 'Gallery image',
      inquiry: 'Inquiry',
      invoice: 'Invoice',
      invoice_bundle: 'Shared invoice file',
      invoice_folder: 'Invoice folder',
      lead: 'Lead',
      library_document: 'Library document',
      mini_site: 'Mini-site',
      offer: 'Offer',
      plan: 'Subscription plan',
      quote: 'Quote',
      reminder: 'Reminder',
      review: 'Review',
      setting: 'Platform settings',
      signature_document: 'Signing document',
      template: 'Template',
      trade: 'Trade',
      user: 'User',
    },
    actorLabel: {
      'Mini-site': 'Mini-site',
      Telephony: 'Telephony',
      system: 'System',
      Worki: 'Worki',
      billing: 'Payment provider',
    },
    auditAction: {
      'customer_payment.merchant_updated': 'Payment provider connection updated',
      'customer_payment.created': 'Customer payment link created',
      'customer_payment.paid': 'Customer payment verified',
      'customer_payment.declined': 'Customer payment declined',
      'customer_payment.cancelled': 'Customer payment request cancelled',
      'user.signup': 'Account created',
      'user.login': 'Signed in',
      'user.reviewCodeIssued': 'Store-review sign-in code issued',
      'account.deleteUser': 'Account deleted by its owner',
      'account.deleteBusiness': 'Business and all its data deleted by its last user',
      'user.password_reset': 'Password reset',
      'profile.update': 'Profile updated',
      'customer.create': 'Customer created',
      'customer.update': 'Customer updated',
      'customer.delete': 'Customer deleted',
      'inquiry.create': 'Inquiry created',
      'inquiry.status_change': 'Inquiry status changed',
      'inquiry.convert': 'Inquiry converted to lead',
      'inquiry.delete': 'Inquiry deleted',
      'note.create': 'Note added',
      'quote.create': 'Quote created',
      'quote.update': 'Quote updated',
      'quote.send': 'Quote sent',
      'quote.view': 'Customer viewed the quote',
      'quote.approve': 'Quote approved',
      'quote.reject': 'Quote rejected',
      'quote.expire': 'Quote expired',
      'quote.delete': 'Quote deleted',
      'quote.lock': 'Quote locked',
      'quote.unlock': 'Quote unlocked',
      'signature.create': 'Signing document created',
      'signature.update': 'Signing document updated',
      'signature.upload': 'File uploaded for signature',
      'signature.place': 'Signature placement marked',
      'signature.send': 'Sent for signature',
      'signature.delivery': 'Signature delivery recorded',
      'signature.signed': 'Document signed',
      'signature.cancel': 'Signature request cancelled',
      'signature.delete': 'Signing document deleted',
      'signature.declined': 'Recipient declined to sign',
      'signature.expire': 'Signing document expired',
      'signature.lock': 'Signing document locked',
      'signature.unlock': 'Signing document unlocked',
      'reminder.create': 'Reminder created',
      'reminder.update': 'Reminder updated',
      'reminder.complete': 'Reminder completed',
      'reminder.delete': 'Reminder deleted',
      'reminder.snooze': 'Reminder snoozed',
      'agent.create': 'Phone agent created',
      'agent.update': 'Phone agent updated',
      'agent.delete': 'Phone agent deleted',
      'template.create': 'Template created',
      'template.update': 'Template updated',
      'template.delete': 'Template deleted',
      'mail_account.connect': 'Email account connected',
      'mail_account.disconnect': 'Email account disconnected',
      'mail_account.reconnect': 'Email account reconnected',
      'mail_account.sync': 'New invoices synced',
      'invoice.delete': 'Invoice deleted',
      'invoice.repaired': 'Invoice details corrected from the original document',
      'invoice.upload': 'Invoice uploaded',
      'invoice.reset': 'All invoices deleted for a fresh scan',
      'invoice.filed': 'Invoices moved to a folder',
      'invoice_folder.created': 'Invoice folder created',
      'invoice_folder.renamed': 'Invoice folder renamed',
      'invoice_folder.emptied': 'Invoice folder emptied',
      'invoice_folder.deleted': 'Invoice folder deleted',
      'invoice_bundle.created': 'Invoices shared as one file',
      'library.add': 'Document added to the library',
      'library.delete': 'Document deleted from the library',
      'library.edit': 'Library document edited',
      'call.inbound': 'Incoming call recorded',
      'call.outbound': 'Outgoing call started',
      'call.update': 'Call updated',
      'call.delete': 'Call deleted',
      'telephony.number.provisioned': 'Virtual number assigned',
      'user.2fa_enable': 'Two-factor authentication enabled',
      'admin.member_invite': 'Team member invited',
      'admin.member_update': 'Team member updated',
      'admin.member_delete': 'Team member removed',
      'subscription.change': 'Subscription changed',
      'billing.checkout_started': 'Subscription payment started',
      'billing.cancelled': 'Subscription cancelled',
      'billing.subscription_activated': 'Subscription activated',
      'billing.payment_succeeded': 'Subscription payment received',
      'billing.payment_failed': 'Subscription payment failed',
      'billing.subscription_cancelled': 'Subscription cancelled by the payment provider',
      'billing.ignored': 'Notice received from the payment provider',
      'site.update': 'Mini-site updated',
      'site.gallery_delete': 'Mini-site gallery deleted',
      'site.image_delete': 'Mini-site image deleted',
      'site.review_delete': 'Mini-site review deleted',
      'site.review_update': 'Mini-site review edited',
      'site.gallery_order': 'Mini-site galleries reordered',
      'site.offer_create': 'Mini-site offer added',
      'site.offer_update': 'Mini-site offer updated',
      'site.offer_delete': 'Mini-site offer deleted',
      'platform.view_business': 'Viewed by Worki staff',
      'platform.list_businesses': 'Worki staff listed businesses',
      'platform.list_users': 'Worki staff listed users',
      'platform.access': 'Worki staff changed access to this account',
      'platform.user_update': 'Worki staff updated a user',
      'platform.user_remove': 'Worki staff deleted a user',
      'platform.business_remove': 'Worki staff deleted this business',
      'platform.staff': 'Worki staff access was changed',
      'platform.member_invite': 'Worki staff invited a user to this business',
      'platform.member_update': 'Worki staff changed a user’s access',
      'platform.member_remove': 'Worki staff removed a user from this business',
      'platform.landing_create': 'A section was added to the landing page',
      'platform.landing_update': 'A landing page section was edited',
      'platform.landing_delete': 'A section was removed from the landing page',
      'platform.landing_move': 'The landing page sections were reordered',
      'platform.lead_update': 'Worki staff updated a lead',
      'platform.package': 'Worki staff updated a subscription plan',
      'platform.settings': 'Worki staff updated the platform settings',
      'platform.trade_create': 'Worki staff added a trade',
      'platform.trade_update': 'Worki staff updated a trade',
    },
  },
  validation: {
    digitalDocument: {"required": "Complete this required field", "email": "Enter a valid email address", "phone": "Enter a valid phone number", "id": "Enter 9 digits", "companyId": "Enter 9 digits", "date": "Enter a valid date", "number": "Enter a valid number", "option": "Choose an available option", "unknownField": "Unknown field", "value": "Enter a valid value (up to 4,000 characters)", "signature": "Sign inside the box", "structure": "Complete the document blocks and field settings"},
    landing: {
      ctaHref: 'The button target must be a path inside the app, for example /signup',
      locale: 'Unknown language',
    },
    signature: {
      title: 'Give the document a name',
      body: 'Write what is being signed',
      signerName: 'Enter a full name',
      image: 'Please sign in the box',
      tooLarge: 'The signature is too large',
      recipientName: "Enter the full name of whoever is signing",
      fileMissing: 'No file was chosen',
      fileType: 'Only PDF or Word (DOCX) files can be uploaded',
      fileTooLarge: 'The file is too large',
      fileUnreadable: 'We could not read this file. If it is password-protected, remove the password and try again.',
      filePages: 'The document has too many pages',
      anchor: 'The signature box falls outside the page',
      anchorPage: 'The signature box points at a page this document does not have',
      anchorCount: 'Too many signature places on one document',
      channel: 'Pick at least one way to send it',
      signer: "The additional signer's details are not valid",
      signerCount: 'Up to four people can be asked to sign one document',
    },
    invoice: {
      amount: 'Enter the amount on the invoice',
      fileMissing: 'Choose a file, or photograph the invoice',
      fileType: 'Upload a PDF or a photograph of the invoice',
      fileTooLarge: 'The file is too large',
      fileUnreadable: 'We could not read this file. Try photographing it again.',
      party: 'Enter the supplier or customer name',
      number: 'Enter the invoice number',
      date: 'Enter the invoice date',
      beforeArchive: 'An invoice from before {{date}} will not show in the list. Fix the date or remove it',
    },
    scan: {
      empty: 'No pages were added to the scan',
      tooManyPages: 'Too many pages in one scan',
      tooLarge: 'The scan is too heavy to send in one go. Split it in two.',
      pageTooLarge: 'One of the pages is too large',
      fileType: 'Attach photographs (JPG or PNG) or PDF files',
      unreadable: 'We could not read one of the pages. Try photographing it again.',
    },
    library: {
      empty: 'The file is empty',
      tooLarge: 'The file is too large',
      fileType: 'PDF, Word and image files can be uploaded',
      // 1.44 — the page editor. The first is a selection the file cannot match; the second is "nothing would be left".
      pages: 'The page selection is not valid',
      noPages: 'At least one page must remain',
    },
    tooLong: 'Value is too long',
    tooLarge: 'Value is too large',
    noSelection: 'Nothing selected',
    noChannel: 'Choose at least one way to send',
    tooManyItems: 'Too many items',
    email: {
      required: 'Enter an email address',
      invalid: 'Invalid email address',
    },
    otp: { invalid: 'Enter a six-digit code' },
    name: { required: 'Enter your full name' },
    businessName: { required: 'Enter a business name' },
    customerName: { required: 'Enter a customer name' },
    rating: { invalid: 'A rating is a whole number from 1 to 5' },
    customer: { required: 'Select an existing customer or enter a new one' },
    quoteTitle: { required: 'Enter a quote title' },
    reminderTitle: { required: 'Enter a reminder title' },
    agentName: { required: 'Enter an agent name' },
    trade: { required: 'Choose a trade' },
    taxId: { invalid: 'A company number is 8 or 9 digits' },
    terms: { required: 'The terms of use must be accepted' },
    templateName: { required: 'Enter a template name' },
    templateBody: { required: 'Enter content' },
    note: { required: 'Enter content' },
    date: { invalid: 'Invalid date' },
    id: { invalid: 'Invalid identifier' },
    token: { invalid: 'This link is not valid' },
    discount: { invalid: 'Invalid discount value' },
    vatRate: { invalid: 'VAT rate must be between 0 and 100' },
    quoteReminderHours: { invalid: 'Value must be between 1 and 168 hours' },
    signatureReminderHours: { invalid: 'Value must be between 1 and 720 hours' },
    approvalCode: { invalid: 'Enter the 4-digit code' },
    rejectionReason: { required: 'Select a reason' },
    phone: { invalid: 'Enter a valid phone number' },
    slug: { invalid: 'Use lowercase English letters, numbers and hyphens only', tooShort: 'A public address needs at least 3 characters' },
    folderName: { required: 'Enter a folder name' },
    galleryName: { required: 'Enter a gallery name' },
    foundedYear: { invalid: 'Invalid founding year' },
    offerName: { required: 'Enter an offer name' },
    validDays: { invalid: 'Validity is a whole number of days from 1 to 365' },
    quoteValidDays: { invalid: 'A quote is valid for 1, 7, 14 or 30 days' },
    item: {
      description: 'Enter an item description',
      quantity: 'Quantity must be greater than zero',
      unitPrice: 'Price cannot be negative',
    },
  },
  /** ADM — Worki's own back office. Read by staff, so it speaks of businesses
   *  in the third person rather than addressing an owner. */
  admin: {
    title: 'Worki admin',
    subtitle: 'What is happening on the platform, and who runs it',
    back: 'Back to admin',
    sections: {
      notifications: 'Notifications',
      users: 'User management',
      approvals: 'Pending and disconnected',
      packages: 'Package management',
      staff: 'Worki staff',
      leads: 'Lead management',
      trades: 'Trades',
      clearing: 'Clearing house',
      reports: 'Reports and statistics',
      settings: 'System settings',
      businesses: 'Businesses',
    },
    about: {
      notifications: 'What needs attention today',
      users: 'Everyone who can sign in, across every business',
      approvals: 'Accounts waiting to open, and accounts that were closed',
      packages: 'Plan prices and the caps that come with them',
      staff: 'Who at Worki may open these screens',
      leads: 'Who asked for an account, and what became of them',
      trades: 'The list the sign-up form offers',
      clearing: 'The payment provider, its charges, and what it tells us',
      reports: "The platform's own numbers over time",
      settings: 'VAT, sign-up approval and the trial length',
      businesses: 'Every business, its plan and its usage',
    },
    notifications: {
      empty: 'Nothing needs attention',
      pendingApproval: 'Waiting for approval',
      pastDue: 'Payment failed',
      trialEnding: 'Trial ending',
      subscriptionEnding: 'Subscription ending',
      cancelled: 'Subscription cancelled',
      dormant: 'No activity',
      newLead: 'New lead',
      waitingDays_one: 'Waiting a day',
      waitingDays: 'Waiting {{count}} days',
      endsInDays_one: 'Ends tomorrow',
      endsInDays: 'Ends in {{count}} days',
      silentDays: 'Quiet for {{count}} days',
      neverActive: 'Never used',
      open: 'Open the business',
      bell: {
        pendingApproval: '{{name}} is waiting for approval',
        pastDue: 'Payment failed at {{name}}',
        trialEnding: "{{name}}'s trial is ending",
        subscriptionEnding: "{{name}}'s subscription is ending soon",
        cancelled: '{{name}} cancelled their subscription',
        dormant: '{{name}} has gone quiet',
        newLead: 'New lead: {{name}}',
      },
      mine: 'My notifications',
    },
    users: {
      searchPlaceholder: 'Search by name, email or business',
      allTypes: 'Everyone',
      onlyStaff: 'Worki staff',
      onlyCustomers: 'Customers',
      allStates: 'All',
      onlyActive: 'Active',
      onlyInactive: 'Suspended',
      staff: 'Worki staff',
      staffHint: 'Access to platform management — every business, not only their own. Every action is filed in the log of the business it touched.',
      rootStaffHint: "This access is set in the server's environment file, so it cannot be revoked here.",
      never: 'Never',
      empty: 'No users found',
      edit: 'Edit user',
      active: 'May sign in',
      remove: 'Delete user',
      removeConfirm: 'Delete {{name}}? This cannot be undone, and the records they created lose their attribution. Suspending keeps it.',
      removeBusiness: 'Delete the user and the business',
      removeBusinessConfirm:
        'This is the only account in {{business}}, so deleting {{name}} deletes the business itself — its customers, quotes, invoices, documents and files. None of it can be restored. Suspending the business is a separate action, and a reversible one.',
      saved: 'User updated',
      removed: 'User deleted',
      removedBusiness: 'The user and the business were deleted',
    },
    approvals: {
      pending: 'Waiting for approval',
      suspended: 'Disconnected',
      pendingEmpty: 'No accounts are waiting for approval',
      suspendedEmpty: 'No accounts are disconnected',
      ended: 'Package has ended',
      endedHint: 'A cancelled subscription or a trial that ran out. The account is open but cannot be worked in until the package is renewed.',
      endedEmpty: 'No account has run out of its package',
      approve: 'Approve',
      suspend: 'Disconnect',
      restore: 'Reconnect',
      approved: 'The account is open',
      wasSuspended: 'The account is disconnected',
      restored: 'The account is open again',
      suspendConfirm: 'Disconnect {{name}}? Everyone signed in is signed out immediately, and nothing is deleted.',
      joined: 'Joined',
      hint: 'Sign-ups only wait for approval while the switch in system settings is on. Otherwise every sign-up opens straight away.',
    },
    packages: {
      price: 'Monthly price',
      priceHint: 'In shekels, before VAT',
      limits: 'Caps',
      connectedMailAccounts: 'Mailboxes',
      galleries: 'Galleries',
      quotesPerMonth: 'Quotes per month',
      voiceActionsPerMonth: 'Quotes dictated to the AI per month',
      unlimited: 'Unlimited',
      unlimitedShort: '∞',
      saved: 'Plan updated',
      hint: 'A change here applies to every business on the plan, including those already paying.',
    },
    staff: {
      root: 'Permanent staff',
      rootHint: "The addresses set in the server's environment file. They cannot be added or removed from a browser — this is the list access is recovered from if everything else is locked out.",
      rootEmpty: 'No addresses are set in the environment file',
      granted: 'Granted from the app',
      grantedHint: 'People given platform access from inside the app. It can be taken back at any time.',
      grantedEmpty: 'Nobody has been granted access from the app',
      manage: 'Granting and revoking happen on the person, in user management',
    },
    leads: {
      searchPlaceholder: 'Search by business, name, email or phone',
      allStatuses: 'All statuses',
      status: {
        new: 'New',
        contacted: 'Contacted',
        converted: 'Became a customer',
        lost: 'Lost',
      },
      empty: 'No leads yet',
      note: 'Note',
      notePlaceholder: 'What was said on the call…',
      saved: 'Lead updated',
      trade: 'Trade',
      hint: 'Everyone who started a sign-up is recorded here, whether or not they finished it.',
    },
    trades: {
      name: 'Trade name',
      position: 'Place in the list',
      positionHint: 'A lower number sits higher in the sign-up form. The shipped list counts in tens, so a trade can be slotted between two others.',
      active: 'Offered at sign-up',
      inactive: 'Not offered',
      add: 'New trade',
      empty: 'No trades yet',
      saved: 'Trade saved',
      hint: 'A trade that is turned off leaves the sign-up form, and the businesses that already chose it keep saying what they do.',
    },
    reports: {
      months: 'The last twelve months',
      signups: 'Sign-ups',
      quotes: 'Quotes',
      invoices: 'Invoices',
      planMix: 'Plan mix',
      businesses: 'Businesses',
      revenue: 'Monthly revenue',
      mrr: 'Monthly recurring revenue',
      approved: 'Quotes approved',
      inquiries: 'Enquiries',
      payingOnly: 'Only paying accounts count towards revenue. A trial is not revenue.',
    },
    clearing: {
      provider: 'Payment provider',
      providerHint: 'The provider that charges subscribers. It is changed in the server configuration, not here.',
      live: 'Live',
      off: 'Billed by hand',
      webhookUrl: 'Address the provider reports to',
      paying: 'Paying subscribers',
      pastDue: 'Past due',
      unattributed: 'Not matched to a business',
      events: 'Recent provider messages',
      eventsHint: 'Every message received, including ones that changed no subscription. A long silence usually means the connection broke.',
      noEvents: 'No messages from the provider',
      notApplied: 'Not applied',
      unknownAccount: 'Unknown business',
    },
    settings: {
      vatRate: 'VAT rate',
      vatRateHint: 'Applies to every quote written from now on. Quotes already written keep their own rate.',
      requireApproval: 'New sign-ups wait for approval',
      requireApprovalHint: 'While this is on, a new account is created but its owner cannot sign in until somebody approves it.',
      trialDays: 'Trial days',
      subscriptionAlertDays: 'Warning before a subscription ends (days)',
      subscriptionAlertDaysHint:
        'How many days ahead a subscription or trial about to end appears in the alerts. Cancellations are listed for the same number of days after they happen.',
      supportEmail: 'Support email',
      saved: 'Settings saved',
    },
  },
  voice: {
    record: 'Voice note',
    recordHint: 'Say what goes in the quote: customer, phone and email, work, address, price',
    editHint: 'Say what to change: lines and prices, customer details, discount, notes',
    dictateHint: 'Speak — the text goes into the field',
    start: 'Start recording',
    stop: 'Finish and send',
    send: 'Send',
    cancel: 'Cancel',
    recording: 'Recording… {{seconds}}s',
    stage: {
      queued: 'Waiting — number {{position}} in line',
      transcribing: 'Transcribing…',
      understanding: 'Working out what you asked…',
      ready: 'Draft ready',
      failed: 'That did not work',
      cancelled: 'Cancelled',
    },
    heard: 'What we heard',
    recordAgain: 'Record again',
    useAsNotes: 'Use as notes',
    useTranscript: 'Use transcript',
    estimated: 'Estimated',
    priceMissing: 'Price missing',
    checkAmount: 'Check amount',
    pickCustomer: 'Who is this quote for?',
    newCustomer: 'New customer: {{name}}',
    editsApplied: 'Changes are in the editor — not saved yet',
    workAddressLine: 'Work address: {{address}}',
    editsSkipped: '{{count}} changes skipped because the lines changed',
    micDenied: 'Microphone permission denied',
    listening: 'Listening…',
    paused: 'Paused',
    pause: 'Pause',
    resume: 'Resume',
    sending: 'Sending…',
  },
  errors: {
    historyOriginalUnavailable: 'This older delivery has no saved link association. It cannot be deleted safely while revoking only its link.',
    voice: {
      notInPlan: 'Voice notes are not part of your plan',
      quotaReached: 'You have used this month\'s quotes dictated to the AI',
      busy: 'The system is busy, try again in a few minutes',
      alreadyRunning: 'A voice note is already being processed',
      tooLong: 'The recording is longer than 2 minutes',
      badFormat: 'The audio file is not supported',
      empty: 'No speech was heard in the recording',
      sttFailed: 'Transcription failed',
      notUnderstood: 'We could not understand the request',
      timeout: 'Processing took too long',
      interrupted: 'Processing was interrupted, record again',
      unavailable: 'The voice service is unavailable right now',
      quoteNotDraft: 'Only a draft can be edited by voice',
    },
    signatureLocked: 'The document is locked',
    quoteLocked: 'This quote is locked',
    quoteExpired: 'This quote has expired, so it cannot be sent again',
    signatureExpired: 'This document has expired, so it cannot be sent again',

    accountPending: 'The account was created and is waiting for Worki to approve it. We will email you the moment it opens.',
    accountSuspended: 'Access to this account is suspended. Contact us to reopen it.',
    unknown: 'An unexpected error occurred',
    network: 'Cannot reach the server',
    validation: 'Some fields need fixing',
    unauthorized: 'Please sign in again',
    forbidden: 'You do not have permission for this action',
    notFound: 'The requested item was not found',
    conflict: 'This action conflicts with the current state',
    mailFailed: 'The email could not be sent. Check the recipient address and try again.',
    signatureSent: 'The document was already sent for signature and cannot be edited. Cancel the request to change it.',
    signatureSigned: 'The document is already signed',
    signatureCancelled: 'The signature request was cancelled',
    signatureDeclined: 'The recipient has already refused to sign this document',
    signatureUnsigned: 'The document has not been signed yet',
    signatureEmpty: 'There is nothing to sign — attach a file before sending',
    signatureNoFile: 'Attach a file before marking where the signature goes',
    docxConverterMissing:
      'Word conversion is not available on this server. Save the document as a PDF and upload that — the result is the same.',
    emailTaken: 'That email address is already registered',
    rateLimited: 'Too many attempts. Try again shortly.',
    quoteNotSendable: 'This quote cannot be sent in its current state',
    quoteAlreadyDecided: 'This quote has already been approved or rejected',
    quoteApprovedLocked: 'An approved quote is kept as the record of the agreement and cannot be deleted',
    quoteLinkExpired: 'The link has expired',
    customerHasDocuments:
      'This customer appears on quotes or invoices that have to be kept, so their contact details were erased and the record itself remains.',
    invoiceBundleEmpty: 'None of the selected invoices is a file that can be attached',
    folderNameTaken: 'A folder by that name already exists',
    libraryFolderNameTaken: 'A folder by that name already exists',
    // 1.44 — the page editor: a screen that opened an older version of the file, and a file with no pages to cut.
    libraryChanged: 'This document was changed on another device. Close the editor and open it again.',
    libraryNotEditable: 'The pages of this file cannot be edited',
    // 1.59 — sharing several documents as one PDF: a file with no pages to put in it, and a selection too heavy to make into one.
    libraryNotMergeable: 'Only PDFs and photos (JPG or PNG) can be shared together. Share a Word file or any other file on its own.',
    libraryMergeTooLarge: 'The selected documents are too large to share as one file. Select fewer, or share them separately.',
    // Section 9 — several files saved as one document. The row of the file is marked, so the words need not name it.
    libraryCombineUnreadable: 'The marked file could not be read. If it is password-protected, remove the password and try again.',
    libraryCombineFileType: 'Only PDF, Word, and JPG or PNG photos can be combined into one document',
    fileTooLarge: 'File is too large',
    fileType: 'Unsupported file type',
    slugTaken: 'This public address is already in use',
    galleryLimit: "You have reached this plan's gallery limit.",
    imageLimit: 'A gallery can contain up to 10 images',
    quoteLimit: "You have reached this plan's monthly quote limit.",
    mailAccountLimit: "You have reached this plan's connected mailbox limit.",
    telephonyProvider: 'The phone provider could not start the call',
    virtualNumberTaken: 'Another user is already assigned this phone number',
    virtualNumberUnknown: 'This business does not hold that number',
    taxIdTaken: 'That company number is already registered with Worki',
    phoneTaken: 'That mobile number is already registered with Worki',
    tradeUnknown: 'The selected trade does not exist',
    tradeTaken: 'A trade with that name already exists',
    googleDisabled: 'Google sign-in is not enabled on this server',
    googleFailed: 'Google sign-in did not complete',
    googleCancelled: 'Google sign-in was cancelled',
    googleUnverified: 'The email address on that Google account is not verified',
    googleExpired: 'That Google sign-up has expired. Please start again.',
    accountDisabled: 'This account is disabled',
    telephonyNotConfigured: 'Automatic number provisioning is not available on this server. Enter a number you already own above.',
    telephonyNoNumbers: 'The phone provider has no number available right now. Try again later or enter one you already own.',
    virtualNumberExists: 'You already have a virtual number. Clear it before provisioning a new one.',
    upstream: 'An external service is unavailable. Try again.',
    billingProvider: 'The payment provider returned an error.',
    billingNotConfigured: 'Payments are not configured.',
    billingWebhookUnverified: "The payment provider's callback could not be verified.",
    billingCancelUnsupported:
      'The cancellation could not be completed with the payment provider and nothing about your subscription was changed. Contact us so we can finish cancelling it.',
    invoiceSyncDisabled: 'Mailbox sync is not enabled on this server.',
    subscriptionInactive: 'The subscription is not active. Renew to keep editing.',
    lastUser: 'This is the last account in the business. Suspending or deleting it would leave the business with no way in — closing the account itself is a separate action, and a reversible one.',
    staffSelf: 'You cannot change the account you are signed in with. Another member of staff can do it.',
    staffRoot: "This access is set in the server's environment file, so it cannot be revoked here.",
    crashTitle: 'Something went wrong on this screen',
    crashBody: 'Your data is untouched. Try loading the screen again, or go back to the dashboard.',
    crashReload: 'Reload',
    crashHome: 'Go to dashboard',
    invoiceScanDuplicate: 'Already in the list',
  },
  mail: {
    loginCodeSubject: 'Your Worki login code',
    loginCodeBody: 'Hello {{name}},\n\nYour Worki login code is: {{code}}\n\nThe code is valid for 10 minutes and can be used once.',
    signupCodeSubject: 'Confirm your Worki account',
    signupCodeBody:
      'Hello {{name}},\n\nYour confirmation code for {{business}} on Worki is: {{code}}\n\nThe code is valid for 10 minutes. The account is created only after you enter it.\n\nIf you did not request this, ignore this email — no account was created.',
    // 1.34 — what the sign-up screen promises a waiting account ("we will email
    // you the moment it opens"). Sent once, when Worki approves it.
    accountApprovedSubject: 'Your Worki account was approved',
    accountApprovedBody:
      'Hello {{name}},\n\nYour Worki account was approved and you can now log in.\n\nTo log in:\n{{link}}\n\nLog in with Google, or with a code we send to the email address you signed up with.',
    quoteSubject: 'Quote #{{number}} from {{business}}',
    // 2.4 — a send carries no file (only an approved quote has one, and it goes
    // out as a copy), so this says the quote was sent and leaves opening it to
    // `quoteLink` below.
    quoteBody: 'Hello {{name}},\n\n{{business}} has sent you quote #{{number}}.',
    // Sent on its own line after the body — a business owner who writes a
    // personal note replaces the body, and without this the mail would go out
    // with no way to open the quote.
    quoteLink: 'To view and approve the quote:\n{{link}}\n\nThe link is valid for {{days}} days.',
    // One day is "one day", not "1 days": the server takes this form for a count of 1.
    quoteLink_one: 'To view and approve the quote:\n{{link}}\n\nThe link is valid for one day.',
    approvedSubjectOwner: 'Quote #{{number}} approved',
    approvedSubjectCustomer: 'Quote #{{number}} approval confirmation',
    approvedBody: 'Quote #{{number}} was approved on {{date}}.\nA signed copy is attached.',
    signatureSubject: 'A document to sign from {{business}}',
    signatureBody:
      'Hello,\n\n{{business}} would like your signature on "{{title}}".\n\nRead and sign:\n{{link}}',
    signedSubject: '"{{title}}" was signed',
    signedBody: '"{{title}}" was signed by {{name}}.\nA signed copy is attached.',
    declinedSubject: 'Document "{{title}}" was declined',
    declinedBody: '{{name}} read the document "{{title}}" and chose not to sign it.\nReason: {{reason}}',
    declinedNoReason: 'No reason given',
    rejectedSubject: 'Quote #{{number}} rejected',
    rejectedBody: 'Quote #{{number}} was rejected. Reason: {{reason}}',
    invoicesSubject: '{{count}} invoices from {{business}}',
    invoicesSubject_one: 'One invoice from {{business}}',
    invoicesSubjectTitled: '{{title}} — {{count}} invoices from {{business}}',
    invoicesSubjectTitled_one: '{{title}} — one invoice from {{business}}',
    invoicesBody:
      'Hello,\n\nAttached is a single file holding {{count}} invoices from {{business}}.\n\nIt is also available here:\n{{link}}\n\nThe link works for 14 days.',
    invoicesBody_one:
      'Hello,\n\nAttached is one invoice from {{business}}.\n\nIt is also available here:\n{{link}}\n\nThe link works for 14 days.',
    invoicesLinkBody:
      'Hello,\n\nHere is a link to a single file holding {{count}} invoices from {{business}}:\n{{link}}\n\nThe link works for 14 days.',
    invoicesLinkBody_one:
      'Hello,\n\nHere is a link to one invoice from {{business}}:\n{{link}}\n\nThe link works for 14 days.',
    // 1.9 — under the body of every message that carries the invoices, one line per currency.
    invoicesTotal: '{{count}} invoices · Invoice total: {{total}} · VAT to reclaim ({{rate}}%): {{vat}}',
    reminderSubject: 'Reminder: {{title}}',
    reminderBody: 'Hi {{name}},\n\nYour reminder "{{title}}" is due.',
  },
  /* Text the server writes into the database on its own, with no user typing it. */
  auto: {
    quoteFollowUpTitle: 'Follow up on quote #{{number}}',
    quoteFollowUpNotes: 'The customer has not approved or rejected the quote yet.',
    signatureFollowUpTitle: 'Follow up on the document "{{title}}"',
    signatureViewedNotes: 'The recipient opened the document and has not signed it.',
    signatureUnopenedNotes: 'The document has not been opened yet — the link may never have arrived.',
    telephonySubject: 'Incoming call',
  },
  history: {
    folderPreviewTitle: 'Sent folder',
    folderFiles: 'Sent files ({{count}})',
    folderManifestUnavailable: 'The original file list was not saved.',
    folderOriginalUnavailable: 'The original sent PDF is unavailable.',
    folderDeleteConfirm: 'Delete {{count}} history entries? The sent public link will be revoked, including other deliveries from the same send. Source files are preserved.',
    resultNumber: 'Result {{number}}',
    channelUnknown: 'Not recorded',
    openForPrint: 'Open PDF to print',
    channel: { email: 'Email', whatsapp: 'WhatsApp', both: 'Email and WhatsApp', copy: 'Copied link' },
    delivery: { delivered: 'Delivered', handoff: 'Complete in WhatsApp' },
    sendFormat: 'Send format',
    formatLink: 'Link',
    formatPdf: 'PDF',
    formatBoth: 'Link and PDF',
    partialDelivery: 'Some channels failed. Retry the failed channels.',

    lock: 'Lock document',
    unlock: 'Unlock document',
    locked: 'Customer access is locked',
    sendCount: 'Sent {{count}} times',
    lockSaved: 'Document access updated',
    sendToAnotherSigner: 'Send to another signer',
    sendCopy: 'Send a copy',
    copySent: 'The copy was sent',
    sendRecipient: 'Customer / recipient name',
    sendChannels: 'Phone / email',
    sendByPhone: 'Phone (WhatsApp)',
    sendByEmail: 'Email',
    sendPickChannel: 'Choose a phone or an email',
    copyEntry: 'Copy',
    copyQuoteSubject: 'A copy of quote #{{number}} from {{business}}',
    copyQuoteBody: 'Hello {{name}},\n\nHere is a copy of quote #{{number}} from {{business}}, as it was approved.',
    copyQuoteLink: 'View the quote:\n{{link}}',
    copyDocumentSubject: 'A signed copy of "{{title}}" from {{business}}',
    copyDocumentBody: 'Hello,\n\nHere is a signed copy of "{{title}}" from {{business}}.\n\nView and download it:\n{{link}}',

    title: 'Send history',
    new: 'New',
    newQuote: 'Quote',
    newDocument: 'Document to sign',
    kindLabel: 'Document type',
    kind: { 'invoice-folder': 'Invoice folders', 'library-folder': 'Document folders', all: 'All', quote: 'Quotes', signature: 'Signing documents' },
    tabHistory: 'Send history',
    tabLibrary: 'My documents',
    deleteConfirm_one: 'Delete the selected item? This cannot be undone.',
    deleteConfirm: 'Delete {{count}} selected items? This cannot be undone.',
  },

  library: {
    saveAndPrint: 'Save and print',
    resultRange: 'Results {{from}}–{{to}} of {{total}}',
    shareFolder: 'Share folder',
    selfSignTitle: 'Sign my document',
    selfSignSave: 'Save signed PDF',
    selfSignHint: 'Draw or import your signature or stamp, then tap the document to place it. Save the signed PDF in My documents to download or share it.',
    moveSomeRefused: '{{count}} documents could not be moved. They remain selected so you can review and try again.',
    title: 'My documents',
    upload: 'Upload documents',
    createDigital: 'Create a digital document',
    searchPlaceholder: 'Search documents…',
    added_one: 'One document added',
    added: '{{count}} documents added',
    sendToSign: 'Send for signature',
    renameTitle: 'Rename document',
    titleLabel: 'Document name',
    deleteTitle: 'Delete document',
    deleteManyTitle_one: 'Delete one document',
    deleteManyTitle: 'Delete {{count}} documents',
    deleteSomeRefused_one: 'One document was not deleted. Refresh the list and try again.',
    deleteSomeRefused: '{{count}} documents were not deleted. Refresh the list and try again.',
    deleteBody: 'The document is removed from the library. Documents already sent for signature are unaffected.',
    emptyTitle: 'No documents yet',
    emptyBody: 'Upload a document or scan one, and it will be here ready to send or sign.',
    allDocuments: 'All documents',
    folders: 'Folders',
    noFoldersYet: 'No folders yet',
    newFolder: 'New folder',
    folderName: 'Folder name',
    renameFolder: 'Rename folder',
    deleteFolder: 'Delete folder',
    deleteFolderNamed_zero: 'Delete the folder "{{name}}"? There are no documents in it.',
    deleteFolderNamed_one: 'Delete the folder "{{name}}"? The document in it is not deleted — it stays among My documents, without a folder.',
    deleteFolderNamed: 'Delete the folder "{{name}}"? The {{count}} documents in it are not deleted — they stay among My documents, without a folder.',
    moveToFolder: 'Move to folder',
    noFolder: 'No folder',
    moveNoFolders: 'There are no folders yet. You can make one from the list of folders.',
    movedTo: 'The document was moved to "{{name}}"',
    movedOut: 'The document was taken out of its folder',
    // 1.44 — the pencil: the document's pages as a grid, where one is taken out
    // with its X and files are added at the end.
    editTitle: 'Edit document',
    addFiles: 'Add files',
    editHint: 'Remove pages with the X, or add files — their pages are added at the end of the document.',
    editPagesFailed: "The document's pages cannot be shown. You can only rename it.",
    // 1.59 — several documents shared at once go out as one PDF, and this is
    // the name it carries in the share sheet ("Documents.pdf").
    mergedName: 'Documents',
    // Section 9 — several files picked together are always one document. The
    // short screen before it is saved: its name, and the files in order, moved
    // with arrows. "Document name" is `titleLabel`.
    combineTitle: 'Save as one document',
    combineHint: 'All the files are saved as one PDF document, in this order. A Word file is converted to PDF.',
    combineOrder: 'File order',
    combining: 'Combining the files into one document…',
    combineRemove: 'Remove file {{index}}',
  },

  invoiceScan: {
    duplicateBlocked: 'This invoice is already saved. Remove it from this scan to continue.',
    duplicateLocation: 'Existing invoice {{number}} · {{folder}}',
    duplicateChanged: 'This invoice already exists. Start a new scan to see where it is saved.',
    choose: 'Choose one invoice or several',
    chooseHint: 'Every file and every photo is saved as an invoice of its own. A PDF of several pages is one invoice.',
    camera: 'Camera',
    cameraHint: 'Photograph the invoices one after another',
    file: 'File',
    fileHint: 'PDF or image, one or several',
    next: 'Continue',
    addFiles: 'Add files',
    preparing: 'Preparing file {{current}} of {{total}}…',
    reading_one: 'Reading the invoice…',
    reading: 'Reading {{count}} files…',
    fileRefused: 'This file cannot be read. Remove it and try again',
    removeFile: 'Remove {{name}}',
    blur: {
      live: 'Blurry — hold steady',
      ask: 'The photo is blurry. Take it again?',
      retake: 'Retake',
      keep: 'Keep',
      mark: 'The photo is blurry',
    },
    count_one: 'One invoice',
    count: '{{count}} invoices',
    total: 'Total',
    totalExpense: 'Expenses',
    totalIncome: 'Income',
    direction: 'Expense or income',
    directionInfo: 'Expense: an invoice the business received from a supplier. Income: an invoice the business issued to its own customer.\nIncome is counted separately: it is not added to the expenses total or to the VAT to reclaim, and the customer name is shown instead of the supplier.',
    expense: 'Expense',
    income: 'Income',
    supplier: 'Supplier',
    customer: 'Customer',
    number: 'Invoice number',
    amount: 'Amount',
    currency: 'Currency',
    date: 'Date',
    untitled: 'Invoice {{index}}',
    edit: 'Edit {{name}}',
    joined: 'Joined · {{count}} pages',
    pages_one: 'One page',
    pages: '{{count}} pages',
    split: 'Split',
    joinPrevious: 'Join to the previous invoice',
    missing: 'Missing: {{fields}}',
    duplicate: 'Already in the list',
    reuseDuplicate: 'Already saved. The existing invoice will be assigned to the selected folder.',
    preview: 'Preview of {{name}}',
    page: 'Page {{index}}',
    pagesCut: 'Showing the first {{shown}} pages of {{total}}',
    remove: 'Remove {{name}}',
    folder: 'Folder',
    folderHint: 'Optional. Every invoice is saved on its own, and a folder groups them for sending.',
    folderNone: 'No folder',
    folderNew: 'New folder…',
    folderName: 'Folder name',
    saveSend: 'Save and send',
    saving_one: 'Saving the invoice…',
    saving: 'Saving {{count}} invoices…',
    blocked_one: 'One invoice needs fixing before saving',
    blocked: '{{count}} invoices need fixing before saving',
    saved_one: 'Invoice saved',
    saved: '{{count}} invoices saved',
  },

  scan: {
    title: 'Quick scan',
    hint: 'Photograph one page after another — each is straightened and cleaned up as it arrives. Finish when you are done.',
    addPhoto: 'Photograph a page',
    addFile: 'Attach a file',
    empty: 'No pages added yet',
    pageCount_one: 'One page',
    pageCount: '{{count}} pages',
    removePage: 'Remove page {{index}}',
    movePageUp: 'Move page {{index}} back',
    movePageDown: 'Move page {{index}} forward',
    create: 'Create PDF',
    done: 'Done',
    addMore: 'Take another',
    // 1.58 — the same dialog on a desktop, where there is no camera: a dropzone in
    // the camera's place, and "Add files" where the review said "Take another".
    // There (section 9) files added together are one document, and a PDF or a
    // Word file on its own is filed as it is, which is what the two lines under
    // the zone say. The ones ending in "Pages" are for a dialog with nowhere to
    // file a document, where every file is a page.
    dropTitle: 'Drop files here, or click to choose',
    dropTypes: 'PDF, Word, JPG or PNG',
    dropHint: 'Files added together are saved as one PDF document. A single PDF or Word file is saved as it is.',
    dropTypesPages: 'PDF, JPG or PNG',
    dropHintPages: 'Add several files at once — they become one PDF, page after page.',
    addMoreFiles: 'Add files',
    fileName: 'Scan',
    name: 'Document name',
    nameHint: 'Optional — without one it is filed as "Scan"',
    camera: {
      starting: 'Starting the camera…',
      waiting: 'Camera is on, but the picture is not showing yet.',
      denied: 'No permission to use the camera. You can attach files instead.',
      unavailable: 'No camera on this device. You can attach files instead.',
      searching: 'Looking for a document…',
      found: 'Hold steady',
      holding: 'Capturing…',
      shutter: 'Take a page',
      auto: 'Auto',
      lens: 'Camera',
      numbered: 'Camera {{index}}',
      lamp: 'Flash',
    },
    editTitle: 'Fix page',
    editPage: 'Fix page {{index}}',
    cropHint: 'Drag the corners until the frame matches the edges of the document.',
    viewPage: 'Scanned page',
    viewCrop: 'Crop',
    previewHint: 'This is how the page will look in the file.',
    preparing: 'Preparing the preview…',
    cropBroken: 'Those corners do not make a page. Fix the crop.',
    rotateLeft: 'Rotate left',
    rotateRight: 'Rotate right',
    wholePhoto: 'Whole photo',
    corner: {
      0: 'Top-left corner',
      1: 'Top-right corner',
      2: 'Bottom-right corner',
      3: 'Bottom-left corner',
    },
    filter: {
      label: 'Look',
      colour: 'Colour',
      mono: 'Black and white',
      original: 'Original',
      info: 'Colour: cleans up the background and shadows and sharpens the page, keeping the colours. This is the recommended one.\nBlack and white: a sharp document, black text on a white background.\nOriginal: the photo as taken, only straightened and cropped to the corners, with no clean-up.',
    },
    applying_one: 'Processing a page…',
    applying: 'Processing {{count}} pages…',
    reviewHint: 'Tap a page to fix its crop or turn it.',
    restored_one: 'Picked up where you left off — one page is waiting.',
    restored: 'Picked up where you left off — {{count}} pages are waiting.',
    ready_one: 'A one-page file was created',
    ready: 'A {{count}}-page file was created',
    filesAdded_one: 'One file added',
    filesAdded: '{{count}} files added',
    making: 'Preparing page {{current}}/{{total}}…',
    merging: 'Creating the file…',
  },
  signing: {
    entryContactRequired: 'Ask the sender for a private link',
    signatureConfirm: 'Confirm',
    entryContinue: 'Continue',
    sendFormat: 'Delivery format',
    sendFormatLink: 'Link',
    sendFormatBoth: 'Link and PDF',
    inkColor: 'Ink color',
    inkBlack: 'Black',
    inkBlue: 'Blue',
    inkWidth: 'Stroke width',
    inkTools: 'Drawing tools',
    inkCancel: 'Cancel drawing',
    inkThicker: 'Increase stroke width',
    inkThinner: 'Decrease stroke width',
    inkColors: {0:'Black',1:'Blue',2:'Green',3:'Red',4:'Purple',5:'Orange',6:'Pink'},
    placeTodayDate: 'Today’s date',
    inkPen: 'Pen',
    inkLine: 'Straight line',
    inkEraser: 'Eraser',
    inkSelect: 'Select and place',
    recipientChipsTitle: 'Signing recipients',
    recipientChipsHint: 'Add the customers who will receive the document for signature',
    recipientChipsEmpty: 'Press + to add a customer',
    recipientEdit: 'Edit {{name}}',
    recipientRemove: 'Remove {{name}}',
    recipientAddTitle: 'Add customer',
    recipientEditTitle: 'Edit customer',
    personalLinkRequired: 'Please ask the sender for your personal signing link.',
    entryIdentityHint: 'Choose your name and confirm the email or phone provided by the sender. If no contact was provided, ask the sender for your private signing link.',
    entryIdentity: 'Who is signing?',
    entryContact: 'Email or phone',
    identityMismatch: 'The details do not match the selected signer.',
    senderDetection: {
      enabled: 'Detect sender signature positions automatically', scanning: 'Looking for sender signature labels…',
      found: 'Added {{count}} sender signature positions. Review and adjust them before sending.',
      none: 'No readable sender signature labels found. Place positions manually; scanned documents require manual placement.',
      failed: 'Could not detect positions in this file. You can continue with manual placement.',
    },
    revisionChanged: 'This document has changed since you opened it. Load the latest version before saving.',
    senderStampUpload: 'Add stamp',
    senderStampPicture: 'Sender stamp',
    senderStampRemove: 'Remove stamp',
    senderSignatureCombinedPreview: 'Signature and stamp preview',
    title: 'Documents to sign',
    subtitle: 'Documents sent to a customer for signature — a safety form, terms, a site-access permit',
    searchPlaceholder: 'Search by document or recipient…',
    new: 'New document to sign',
    edit: 'Edit document',
    document: 'Document',
    documentName: 'Document name',
    documentNameHint: 'How the document is listed and named in the email. Defaults to the file name.',
    documentNameSearchHint: 'Type a new name, or search a document you have already uploaded — the arrow opens the list',
    pickFiledDocument: 'Choose from my documents',
    /* 1.23 — several of them at once: a box on every row of that list, and at its
       foot a line saying so, which becomes this button once any is ticked. */
    pickFiledManyHint: 'Tick several documents to add them together',
    pickFiledAdd_one: 'Add one document',
    pickFiledAdd: 'Add {{count}} documents',
    /* §7 (05.10) — on a desktop the list opens from a labelled button inside the
       name box; the X beside it empties a typed name; and the line under the
       box when the list could not be fetched. */
    myDocuments: 'My documents',
    documentNameClear: 'Clear the name',
    libraryLoadFailed: "We couldn't load your documents.",
    /* Read-only now. Nothing writes a typed-out document any more; the ones
       written before SIG-003 still open, and this labels their text. */
    body: 'Document text',
    /* SIG-003 — the recipient, typed rather than picked. */
    recipient: 'Who signs',
    recipientName: 'Full name',
    recipientPhone: 'Phone',
    recipientEmail: 'Email',
    recipientHint: 'If the number or address is already known, the document is linked to that customer automatically.',
    linkedCustomer: 'Linked to an existing customer: {{name}}',
    statusLabel: 'Status',
    /* SIG-003 — the file and where the signature goes on it. */
    fileHint: 'PDF, Word (DOCX) or a photo. Pick several and they become one document to sign.',
    fileAdd: 'Add a file',
    /* 1.22 — beside "add a file", under the document's name. */
    digitalCreate: 'Create digital document',
    filesMerged: '{{count}} files merged into one document',
    fileReplace: 'Replace the file',
    filePages_one: '1 page',
    filePages: '{{count}} pages',
    fileUploading: 'Uploading and converting…',
    fileReplaceWarning: 'Replacing the file clears the signature places already marked.',
    /* 1.29, 1.30 — taking a file, or the pick of one, back out. */
    fileCancel: 'Cancel the choice',
    fileRemove: 'Remove the file',
    fileRemovedBoxesCleared: 'The file was removed. The signature places were cleared because the pages changed.',
    place: 'Choose where to add a signature or text',
    placeDragHint: 'Drag to position it exactly',
    placeCount_one: 'One signing area',
    placeCount: '{{count}} signing areas',
    placeRemove: 'Remove this place',
    placeClear: 'Clear all places',
    /* 1.47 — a saved signature picked under the pages, with no place marked for the sender's signature to take it. */
    placeSavedTap: 'Tap the document to place this signature.',
    placeSavedLimit: 'The document has reached its area limit. Remove an area before placing this signature.',
    placeSavedNoBox: 'To add a saved signature, first add a "Sender’s signature" area on the page.',
    placeBigger: 'Larger',
    placeSmaller: 'Smaller',
    placeText: 'Free text',
    /* 1.33 — the box's width, read out between bigger and smaller. */
    placeSizeLabel: 'Width of the place: {{value}}% of the page width',
    /* 1.1 — a text field the sender fills in before sending. */
    placeSenderText: 'Type the text here',
    senderTextMissing: 'A place marked "Fill it in yourself" is still empty. Fill it in or remove it, then send.',
    /* 1.1, redone — what goes in a place, as each box says it. */
    placeRoleSignerSignature: 'Customer’s signature',
    placeRoleSenderSignature: 'Sender’s signature',
    placeRoleSignerText: 'Text the recipient fills in',
    /* 1.1, a third time — the box goes down first, and its bar says what goes in it. */
    placeSignatureMenu: 'A signature here',
    placeTextMenu: 'Text here',
    placeSignSelf: 'Sender’s signature',
    placeSignByRecipient: 'Customer signature',
    placeFillSelf: 'Fill it in yourself',
    placeFillByRecipient: 'Filled in by the recipient',
    senderSignatureTitle: 'My signature',
    senderSignatureHint: 'Sign inside the frame, or choose a saved signature',
    senderSignatureSignHere: 'Sign here',
    senderSignatureSave: 'Save the signature',
    senderSignatureRedo: 'Sign again',
    senderSignatureMissing: 'A place marked "Sender’s signature" has not been signed yet. Sign it or remove it, then send.',
    senderSignatureUpload: 'Upload signature',
    senderSignatureDraw: 'Draw instead of the picture',
    senderSignaturePicture: 'The signature from the picture',
    senderSignatureNoInk: 'No signature was found in the picture. Photograph a signature in dark pen on white paper, up close.',
    senderSignatureUnreadable: 'The picture could not be read. Try another PNG or JPEG file.',
    senderSignatureRemember: 'Save the signature for reuse',
    senderSignatureSavedList: 'Saved signatures',
    senderSignatureAdd: 'Add',
    senderSignatureNoneKept: 'No saved signatures yet',
    senderSignatureListFull: 'Up to {{max}} signatures can be saved. Remove one to add another.',
    senderSignatureUseKept: 'Use the kept signature',
    senderSignatureForget: 'Remove from this device',
    senderSignatureForgetFailed: 'The signature could not be removed from this device.',
    senderSignatureKeepFailed: 'The signature could not be saved on this device.',
    senderSignatureNotKept: 'The signature was added to the document, but this device would not keep it for next time.',
    /* SIG-003 — more than one person on the same page. */
    placeForNextSigner: 'A place for the next signer',
    placeDuplicate: 'Duplicate for the same signer',
    placeCopy: 'Duplicate field',
    stampColorMode: 'Stamp colors',
    stampOriginal: 'Original colors',
    stampBlackWhite: 'Black and white',
    placeSigner: 'This box belongs to signer {{number}} — press to pass it to the next',
    signerNumber: 'Signer {{number}}',
    signerAdd: 'Add another signer',
    signerRemove: 'Remove this signer',
    signerNameMissing: 'Enter a name for signer {{number}}',
    signerLinks: 'The signing links',
    signerWaiting: 'Not signed yet',
    placeSaving: 'Saving…',
    placeSaved: 'Signature places saved',
    preview: 'Preview',
    previewSigned: 'The signed copy',
    signer: 'Signed by',
    signedAt: 'Signed at',
    notViewed: 'Not opened yet',
    viewedTimes_one: 'Opened once',
    viewedTimes: 'Opened {{count}} times',
    send: 'Send for signature',
    resend: 'Send again',
    saveDraft: 'Save as draft',
    draftKept: 'Saved as a draft',
    numberBadge: 'Signing document {{number}}',
    sentAtLabel: 'Sent {{when}}',
    signedBy: 'Signed by {{name}} · {{when}}',
    declinedAtLabel: 'Declined {{when}}',
    /* The one date at the top of a sent document is its latest event: made,
       sent (`sentAtLabel`), signed, or declined (`declinedAtLabel`). */
    detailCreated: 'Created {{when}}',
    detailSigned: 'Signed {{when}}',
    declineReason: 'Reason given: {{reason}}',
    sendVia: 'Send via',
    channelEmail: 'Email',
    channelWhatsapp: 'WhatsApp',
    sendTo: 'Send to address',
    sendToHint: "Leave empty to use the address on the document. With no address at all the link is still created.",
    sendToPhone: 'Send to number',
    sendToPhoneHint: 'Leave empty to use the number on the document. With no number at all the link is still created.',
    whatsappReady: 'WhatsApp is open with the message — press send',
    sent: 'Sent for signature',
    linkReady: 'The link is ready to pass on',
    /* SIG-004 — the three endings a send has, kept apart on purpose: a mail the
       server accepted, a message still sitting in the sender's own WhatsApp,
       and a link nothing was done with. Rounding all three up to "sent" is how
       a business finds out a week later that nobody was asked to sign. */
    deliveredTo: 'Sent to {{to}}',
    /* SIG-004 — the composing screen. */
    converting: 'Preparing the document…',
    composeEmpty: 'Choose a file and its pages appear here to be marked',
    sendByEmail: 'Send by email',
    sendByWhatsapp: 'Send on WhatsApp',
    sendChannels: 'Send on',
    sendOptions: 'Send options',
    pickAChannel: 'Pick at least one way to send it',
    needEmail: 'Enter an email address to send by email',
    needPhone: 'Enter a phone number to send on WhatsApp',
    validDays: 'Link valid for',
    validDaysOption_one: 'One day',
    validDaysOption: '{{count}} days',
    validDaysDefault: 'Default ({{count}} days)',
    followUp: 'Chase if unsigned',
    hoursShort: '{{count}}h',
    followUpDefault: "The business's own setting",
    followUpEvery_one: 'Remind if unsigned within an hour',
    followUpEvery: 'Remind if unsigned within {{count}} hours',
    leaveTitle: 'Leave without sending?',
    leaveBody: 'Nothing has been saved yet — the file, the places marked on it and the details typed here all go.',
    link: 'Signing link',
    cancel: 'Cancel the request',
    download: 'Download the signed document',
    deleteTitle: 'Delete the document',
    deleteBody: 'The document will be removed from history and the customer viewing link will stop working.',
    emptyTitle: 'Nothing to sign yet',
    emptyBody: 'Documents sent to customers for signature are kept here, and the signed copy comes back here.',
    /* At the top of the screen opened from "send to another signer". */
    duplicateNotice: 'The same document, sent again to another signer. Enter their contact details; this send receives its own document number and signing link.',
    duplicateForAnotherSigner: 'Duplicate for another signer',
    /* The digital document: its builder, its preview, and what the compose
       screen shows only for one. */
    digital: {
      quickDefaults: 'Add customer fields', bold: 'Bold paragraph', underline: 'Underline paragraph',
      alignLeft: 'Align left', alignCenter: 'Center', alignRight: 'Align right', numberedList: 'Number paragraphs', divider: 'Divider',
      importTerms: 'Import service terms', importFromLibrary: 'Import from my documents',
      importTermsHint: 'Text file or searchable PDF. Imported text remains editable.',
      importTermsFailed: 'Could not import text from this file', importNoText: 'This file contains no extractable text. You can paste the terms manually.',
      importTooLarge: 'The file or text is too large to import', terms: 'Service terms', agreementText: 'I accept the service terms', yes: 'Yes', no: 'No',
      backToFile: 'Upload or scan',
      contactHint: 'Contact details for delivering the link. The customer completes the document fields themselves.',
      previewBeforeSend: 'Preview before sending',
      title: 'Write your document',
      intro: 'Write naturally. Type @ wherever your customer needs to fill in a detail.',
      addField: 'Fillable field',
      undo: 'Undo',
      redo: 'Redo',
      inlineHint: 'Fillable fields stay inside your sentences',
      editorLabel: 'Document content',
      placeholder: 'For example: This agreement is signed by @Full name, ID @ID number',
      example: 'Example: This agreement is signed by @Full name, ID @ID number',
      editField: 'Click to edit this field',
      fieldSettings: 'Field settings',
      chooseField: 'Choose a fillable field',
      backToEditing: 'Back to editing',
      notSaved: 'This document has not been saved or sent.',
      previewSize: 'Preview size',
      desktop: 'Desktop',
      mobile: 'Mobile',
      finalNumber: 'The final number and date are assigned when sent.',
      customerSigns: 'The customer signs here',
      document: 'Digital document',
      customerPreview: 'Customer preview',
      fields: 'Customer fields',
      heading: 'Heading',
      paragraph: 'Paragraph',
      fieldCount: 'fields to complete',
      requiredCount: 'required fields',
      fieldLabel: 'Field label shown to the customer',
      fieldType: 'Answer type',
      requiredBeforeSigning: 'Required before signing',
      fieldHint: 'Example or hint inside the field',
      choices: 'Choices',
      choicesHint: 'One choice per line, up to 20 choices.',
      optionOne: 'First option',
      optionTwo: 'Second option',
      remove: 'Remove',
      previewHint: 'Try completing the fields here. These sample answers are never sent.',
      emptyPreview: 'Add some content to see the customer preview.',
      documentNameExample: 'For example: Work completion approval',
      deliveryStep: 'Choose the recipient',
      presets: {
        city: 'City', agreement: 'Accept service terms', checkbox: 'Checkbox', yesNo: 'Yes or no',
        name: 'Full name',
        phone: 'Phone',
        id: 'ID number',
        companyId: 'Company number',
        email: 'Email',
        address: 'Address',
        date: 'Date',
        signature: 'Signature',
        custom: 'Custom field',
      },
      types: {
        text: 'Short text',
        textarea: 'Long text',
        phone: 'Phone number',
        email: 'Email address',
        id: 'ID number',
        companyId: 'Company number',
        date: 'Date',
        number: 'Number',
        checkbox: 'Checkbox',
        select: 'Dropdown',
        signature: 'Handwritten signature',
      },
    },
    /* SIG-005, 1.50 — what the signer is told while the picture they picked is read. */
    assets: {
      preparing: 'Preparing the image…',
      rejected: 'Choose a PNG or JPG signature or stamp, up to 5 MB.',
      tooDetailed: 'We could not shrink this image. Photograph just the signature or stamp, on a light background.',
    },
    status: {
      draft: 'Draft',
      sent: 'Sent/pending',
      viewed: 'Opened',
      signed: 'Signed',
      declined: 'Declined',
      expired: 'Expired',
      cancelled: 'Cancelled',
    },
  },
  publicSign: {
    creatorSessionNotice: 'You are signed in to the account that created this signature request. You can continue and sign using this link.',
    signersTitle: 'Signers',
    waitingTitle: 'Your signature was saved',
    waitingBody: 'The document is waiting for the other signers. You will be notified when everyone has signed. Have a good day.',
    revisionChanged: 'This document was updated. Load and review the latest version before signing.',
    reloadLatest: 'Load latest document',
    notFound: 'Document not found',
    signed: 'Signed',
    signedBody: 'Thank you, {{name}}. A signed copy was sent to the business, and you can keep one here.',
    download: 'Download a signed copy',
    cancelled: 'The signature request was cancelled',
    expired: 'The link has expired',
    closedBody: 'Contact the business that sent the document for a new link.',
    decline: "I don't want to sign",
    declineReason: 'Reason (optional)',
    declineReasonHint: 'The business will see what you write here. You can send it without a reason.',
    declineConfirm: 'Send refusal',
    declined: 'The document was declined',
    declinedBody: 'We told the business you chose not to sign. If that was a mistake, contact them for a new link.',
    signerName: 'Full name',
    drawHere: 'Signature',
    drawHint: 'Sign inside the box with a finger or the mouse.',
    clear: 'Clear',
    undo: 'Undo',
    uploadMark: 'Upload or photograph a stamp or signature',
    saveMark: 'Save',
    pickedMark: 'The picked image',
    removeMark: 'Remove the picture',
    submit: 'Confirm and sign',
    /* SIG-003 — the uploaded file, read before signing. */
    documentHint: 'Read the whole document before signing. Your signature is stamped where the boxes are marked.',
    signHere: 'Signature here',
    typeHere: 'Fill in here',
    documentTextHint: 'This document has fields to fill in. Complete them on the document itself, then sign below.',
    signedDocumentHint: 'This is the signed copy, with the signature where it was marked.',
    /* SIG-003 — a document more than one person signs. */
    yourBoxes: 'The places marked {{number}} are yours. The rest belong to the other signers.',
    waitingOnOthers: 'The document is still waiting on: {{names}}',
  },
  /** MKT-001 — the marketing page. See he.ts for why the copy lives here. */
  landing: {
    signIn: 'Sign in',
    signInTitle: 'Sign in to Worki',
    signInHint: 'No password. We email you a code, or you continue with Google.',
    noAccount: "Don't have an account yet?",
    signedInTitle: 'Hello {{name}}',
    signedInCta: 'Open Worki',
    plansTrial: '{{days}} days of full access, no credit card',
    editor: {
      badge: 'Editing',
      hint: 'An edit replaces the original text in the current language only.',
      edit: 'Edit section',
      add: 'Add section',
      newSection: 'New section',
      remove: 'Delete section',
      removeConfirm: 'Delete this section?',
      hide: 'Hide from visitors',
      show: 'Show to visitors',
      hidden: 'Hidden',
      reset: 'Restore the original text',
      resetConfirm: 'Discard this edit and restore the original text?',
      kind: 'Section type',
      eyebrow: 'Overline',
      heading: 'Heading',
      body: 'Text',
      bullets: 'Lines',
      bulletsHint: 'One line per row.',
      ctaLabel: 'Button label',
      ctaHref: 'Button target',
      ctaHrefHint: 'A path inside the app, for example /signup',
      empty: 'This page has no sections yet.',
    },
    kind: {
      hero: 'Opening',
      feature: 'Capability',
      plans: 'Plans',
      cta: 'Call to action',
    },
    sections: {
      hero: {
        heading: 'The one system that actually helps you close more deals',
        body: 'Everything a business owner needs to turn an enquiry into a deal — in one place.',
        bullets: ['Smart quotes', 'Portfolio', 'Customer tracking', 'Lead management', 'AI phone agent (coming soon)'],
        ctaLabel: 'Start now',
      },
      quotes: {
        heading: 'Send the quote and stop guessing',
        body: 'A fast, simple way to send professional quotes in moments, shown to the customer in a form that sells.',
      },
      tracking: {
        eyebrow: 'When you know exactly where the quote stands',
        heading: 'All of it in front of you, live',
        bullets: [
          'Did the customer open the quote?',
          'How long did they spend on it?',
          'An alert the moment it is approved or declined',
        ],
      },
      portfolio: {
        eyebrow: 'Suddenly the quote is far more than a generic document',
        heading: 'Your portfolio sits inside the quote',
        body: 'Presented properly — because before a customer buys the price, they have to know who you are.',
      },
      invoices: {
        eyebrow: 'Less handling, more control',
        heading: 'Invoices are pulled from your own mailboxes',
        body: 'Connect your mailboxes and let invoices arrive on their own. Less searching, less paper — everything here, now.',
      },
      ai: {
        eyebrow: 'And the headline act — when you do not pick up (coming soon)',
        heading: 'Your AI receptionist answers, in clear Hebrew',
        body: 'Busy, in a meeting, abroad or simply unavailable — the business keeps working and no customer is lost.',
        bullets: ['The customer calls', 'The AI answers and takes down the enquiry', 'You get the whole story, plainly'],
      },
      plans: {
        heading: 'Plans',
        body: 'Start small and move up a plan as the business grows.',
      },
      closing: {
        eyebrow: 'One system. Less handling. More deals.',
        heading: 'Worki',
        ctaLabel: 'Start now',
      },
    },
  },
  legal: {
    updated: 'Last updated: 5 November 2026',
    contact: 'darom.nadlan@gmail.com',
    back: 'Back',
    deleteAccount: {
      title: 'Deleting your Worki account',
      intro:
        'You can request account deletion inside Worki or by email. This page explains the process and the records that may remain.',
      sections: [
        {
          heading: 'From inside the app',
          body: [
            'Open Profile, scroll to "Delete account", and type your email address to confirm. Account deletion cannot be undone. File removal can continue in the background and may require retries after a storage failure.',
            'Worki is the same account on the web, on Android and on Windows. Deleting it in one deletes it everywhere, because there is one account behind all three.',
          ],
        },
        {
          heading: 'If you cannot sign in',
          body: [
            'Email darom.nadlan@gmail.com to request deletion. We need to verify that the request comes from the account holder before acting. If you cannot access the registered address, explain this so we can assess another way to verify your identity.',
          ],
        },
        {
          heading: 'What is deleted',
          body: [
            'If you are the last person in your business, the business goes and everything filed under it goes with it: your customers, enquiries, quotes and their line items, invoices and the folders holding them, the portfolio and the mini-site with its images and reviews, connected mailboxes and their access, saved documents and signatures, call records, reminders, notes, notifications, team invitations and the subscription record. Associated files are scheduled for removal; removal may finish after the account closes.',
            'If other people still use the same business, only your own user is deleted. The business and its work stay with the people still in it — deleting a colleague\'s quotes is not what leaving a company means.',
          ],
        },
        {
          heading: 'What is kept, and why',
          body: [
            'The activity log is append-only in the current system. Entries, including the name of the person who acted, can remain after account deletion as evidence of actions and security events. Retention requirements and requests concerning these entries need individual review. We do not promise immediate removal of every record or backup.',
            'Emails already sent — a quote to a customer, a login code — are in the recipient\'s mailbox and out of our reach. Deleting your account does not recall them.',
          ],
        },
      ],
    },
    privacy: {
      title: 'Privacy policy',
      intro:
        'Worki is a system for running a small business: enquiries, quotes, a portfolio, invoices and calls. This document explains what is collected, why, who it goes to and how long it is kept.',
      sections: [
        {
          "heading": "Required information and optional connections",
          "body": [
            "Account and contact information is needed to create and use an account. You choose what business and customer content to provide; missing information can prevent the related function from working. Gmail, contacts, notifications, voice and AI connections are optional. Declining one prevents that feature from working, rather than authorising access to other information.",
            "The contacts picker runs on your device. Only details you select and submit to a Worki form are sent; Worki does not upload your entire address book. Voice and AI requests can send audio, text and the context needed for the requested action to a remote service. Do not include information you are not authorised to provide.",
            "Google Calendar integration is currently disabled in production. If enabled in future, connecting it will require the relevant permission. Necessary session cookies and local preferences support sign-in and the interface."
          ]
        },
        {
          "heading": "Service identity and contact",
          "body": [
            "Worki is the product name. It is not a statement that a company or registered business named Worki or worki-top exists. The activity has not yet been registered; full operator identification and a business address have not yet been published. For service and privacy requests, contact darom.nadlan@gmail.com."
          ]
        },
        {
          heading: 'Two kinds of data, two responsibilities',
          body: [
            'Data about you — the account holder and the people in your business — is collected by Worki in order to run the service, and we are responsible for it.',
            'You enter customer information such as names, phone numbers, addresses and quotes. Worki processes it to provide the functions you request, including sending documents and recording approvals. You are responsible for having authority to supply it and contact its subjects. Worki also processes activity and security records to operate and protect the service.',
          ],
        },
        {
          heading: 'What is collected about you',
          body: [
            'At sign-up: name, email address, business name, trade, and a tax ID if you entered one. Signing in is by a one-time code to your email or through a Google account — there is no password, so there is no password to lose.',
            'Actions in the system are recorded in an append-only activity log. Entries can include your name and may remain after account deletion; see the retention section.',
          ],
        },
        {
          heading: 'Quote tracking and the mini-site',
          body: [
            'When a customer opens a quote you sent, we record when it was opened, how many seconds it stayed open, the browser type, and a one-way fingerprint of the IP address. This quote-view record uses a fingerprint rather than the original IP address.',
            'On the mini-site a daily visitor fingerprint is kept, and its only job is to stop the same visitor being counted twice on one day.',
            'When a customer approves a quote or signs a document, the fingerprint and browser are kept as evidence of that approval — not as telemetry — and age out with the document.',
          ],
        },
        {
          heading: 'Mailboxes and invoices',
          body: [
            'If you connected a Gmail or Microsoft mailbox, Worki scans it to find invoices. So that each scan does not download the mail again, Worki keeps a copy of the messages the scan checks: sender, subject, date, the message body and the attached PDF files. For every invoice found it also keeps the invoice document and what was extracted from it: supplier, number, amount, currency and date. The copy covers only the last four years the scan reads.',
            'Gmail is connected with read-only access for locating and displaying invoices. Scanned sender, subject, dates, message bodies and PDF attachments can be cached on our infrastructure. Gmail data is not sold, used for advertising or used to train AI models. Use of Google API data is subject to the Google API Services User Data Policy, including Limited Use requirements.',
            'Recognising an invoice happens on our own server — text extraction from PDFs and character recognition on images. No mail content is sent to an external artificial-intelligence provider.',
            'You can disconnect a mailbox from the invoices screen at any time, and you can also revoke the permission directly on your Google or Microsoft account permissions page. Disconnecting stops all future scans and deletes the stored access tokens, the copy of the messages and the invoices scanned from that mailbox.',
          ],
        },
        {
          heading: 'Calls and virtual numbers',
          body: [
            'For a virtual number assigned to the business we keep the call details: calling number, destination number, direction, duration, time and outcome. The call itself runs at the telephony provider; Worki does not record calls and does not keep transcripts.',
            'The AI agent settings — name, voice and the questions it should ask — are text you write, kept with the rest of your business settings.',
          ],
        },
        {
          heading: 'Payments',
          body: [
            'Payment happens on the payment processor’s own secure page. Card details do not pass through Worki and are not stored by us. What is kept is the subscription state, the plan, and a record of the messages the processor sent us.',
          ],
        },
        {
          heading: 'Who the data goes to',
          body: [
            'Google — account sign-in, and reading invoices from Gmail when you connected a mailbox.',
            'Microsoft — reading invoices from an Outlook mailbox when you connected one.',
            'The telephony provider — WhatsApp messages, virtual numbers and calls.',
            'A payment processor — when a payment feature is enabled. Automatic Worki subscription billing is currently disabled.',
            'The outgoing mail server — sending sign-in codes, quotes and reminders.',
            'Oracle Cloud hosts the primary application and database in Jerusalem (il-jerusalem-1). Optional voice and AI functions can use a remote Modal service whose processing region has not been verified. Google Firebase Cloud Messaging receives device tokens and generic notifications for push delivery. Data can therefore be processed outside Israel; the primary server location does not describe every provider or copy. Data may also be disclosed where required by law or a court order.',
          ],
        },
        {
          heading: 'Security',
          body: [
            'Connections to the public service use HTTPS. Mailbox secret fields are encrypted at the field level. Oracle documents encryption at rest for its Block and Boot volumes. We have not completed verification of every backup, volume mapping or copy, and do not claim that every stored copy has been independently verified.',
            'Quote-view and mini-site analytics use keyed fingerprints to reduce direct identification. Signing evidence may include a connection fingerprint and browser details.',
            'The connection to the system is encrypted, and a session expires on its own. You can sign out every connected device from your personal area.',
          ],
        },
        {
          "heading": "How long data is kept",
          "body": [
            "Business records are generally kept while the account is active. Some temporary codes, view identifiers, notifications and caches have automated cleanup. Operational cleanup periods are not a legal determination of how long every category must be retained.",
            "The append-only activity log can remain after account deletion. Shared business records remain when other team members still use the business. Removing files can require background retries, and copies already sent to recipients cannot be recalled. Backup locations, deletion schedules and retention obligations have not yet been fully verified. Contact us for a request concerning a particular record."
          ]
        },
        {
          heading: 'Your rights',
          body: [
            'Under the Israeli Protection of Privacy Law, 5741-1981, you may inspect the data held about you, ask for incorrect data to be corrected, and ask for it to be deleted. Most of the data is directly available to you in the system and can be edited or deleted from there.',
            'To delete the account entirely, or for any other request, contact us at the address at the foot of this page. We will answer within a reasonable time.',
          ],
        },
        {
          heading: 'Minors',
          body: ['The service is intended for business owners and is not meant for use by anyone under 18.'],
        },
        {
          heading: 'Changes to this policy',
          body: [
            'If we change the policy we will update the date at the top of this page, and where the change is material we will give notice in the system before it takes effect.',
          ],
        },
      ],
    },
    terms: {
      title: 'Terms of use',
      intro: 'These terms govern the use of Worki. Opening an account or using the system means accepting them.',
      sections: [
        {
          "heading": "Electronic signatures and documents",
          "body": [
            "Worki records a drawn electronic signature and related evidence such as name, time, connection fingerprint and browser. This is not a certification-authority certificate or a claim of a certified electronic signature. The suitability and evidential value depend on the document and circumstances; some uses require a different signing method.",
            "Invoice imports, OCR and reports are organisational tools. They do not by themselves establish permission to destroy an original, deduct tax, or use Worki as an approved bookkeeping system. Check the requirements for your activity before relying on them."
          ]
        },
        {
          "heading": "Service identity and contact",
          "body": [
            "Worki is the product name. It is not a statement that a company or registered business named Worki or worki-top exists. The activity has not yet been registered; full operator identification and a business address have not yet been published. For service and privacy requests, contact darom.nadlan@gmail.com."
          ]
        },
        {
          heading: 'The service',
          body: [
            'Worki provides tools for managing enquiries, quotes, a portfolio, invoices, documents for signature and inbound calls. Which capabilities you get depends on the plan you chose.',
          ],
        },
        {
          heading: 'Your account',
          body: [
            'The account belongs to the business, and you are responsible for what is done in it.',
            'Signing in is by a code to your email or through Google. Keeping control of your mailbox is your responsibility, because it is the key to the account.',
          ],
        },
        {
          heading: 'Your content and your customers',
          body: [
            'What you upload — prices, images, customer details — stays yours. We hold it in order to provide the service and put it to no other use.',
            'You are responsible for having the right to enter your customers’ details and to send them messages, and for complying with the law that applies to the mail you send.',
          ],
        },
        {
          heading: 'Prohibited use',
          body: [
            'The system may not be used for unsolicited bulk mail, impersonation, publishing abusive or unlawful content, attempting to reach another account, or deliberately loading the service.',
            'Breaching this clause allows us to suspend the account, and in a serious case to close it.',
          ],
        },
        {
          heading: 'Trial, plans and payment',
          body: [
            'Automatic Worki subscription billing is currently disabled. Displayed plans do not by themselves activate a charge. Before paid subscriptions are enabled, the total payable price, applicable tax treatment, renewal, cancellation, access and refund terms must be presented for acceptance.',
            'For account closure or questions about any payment, contact darom.nadlan@gmail.com. These terms do not promise end-of-period access or limit rights that cannot be waived under applicable law.',
          ],
        },
        {
          heading: 'Availability',
          body: [
            'We aim to keep the service continuously available, but availability is not guaranteed. We may carry out maintenance, and will prefer hours when use is low.',
          ],
        },
        {
          heading: 'Intellectual property',
          body: ['The software, the name, the logo and the design are ours. They may not be copied, sold, or made into a competing product.'],
        },
        {
          heading: 'Liability',
          body: [
            'The service is provided as is. We are not liable for indirect damage, lost profit or lost data arising from use of the service or from its interruption.',
            'In any event our total liability will not exceed the amount you paid in the twelve months preceding the event.',
          ],
        },
        {
          heading: 'Termination',
          body: [
            'You may close the account at any time. We may close an account for breach of these terms or for non-payment, after notice.',
            'Account deletion follows the public account-deletion page. Shared business records can remain for other team members, the append-only activity log can survive closure, and file deletion may require background retries. Copies held by recipients are outside our control.',
          ],
        },
        {
          heading: 'Governing law',
          body: ['Israeli law applies, subject to mandatory protections and jurisdiction rules that cannot be waived.'],
        },
      ],
    },
    accessibility: {
      "title": "Accessibility statement",
      "intro": "This statement describes existing accessibility features, known limitations and how to request help. It is not a certification of compliance or a declaration of an exemption.",
      "sections": [
        {
          "heading": "Features available today",
          "body": [
            "The interface includes labelled controls, keyboard support in many components, a skip link in the application, and reduced-motion styles. Hebrew and English are available. Coverage varies between screens and these features alone do not establish compliance with an accessibility standard."
          ]
        },
        {
          "heading": "Limitations and assessment",
          "body": [
            "A complete professional assessment of keyboard navigation, screen readers, contrast and all workflows has not been completed. Signing canvases, uploaded documents, generated PDFs and the Android and Windows apps require further assessment. We cannot currently claim conformance to Israeli Standard 5568 at level AA or a legal exemption."
          ]
        },
        {
          "heading": "Request help or report a barrier",
          "body": [
            "Email darom.nadlan@gmail.com with the page or feature, what prevented you from completing your task, your device and browser, and the format or assistance you need. Avoid sending unnecessary personal or confidential information. You can request an explanation in text or an alternative way to complete an action; we will assess the available assistance with you.",
            "This email is the public service contact for accessibility requests. No named accessibility coordinator or fixed response time is represented here."
          ]
        }
      ]
    },
  },
  pdf: {
    quote: 'Quote',
    number: 'Number',
    date: 'Date',
    to: 'To',
    description: 'Description',
    quantity: 'Qty',
    unitPrice: 'Price',
    lineTotal: 'Total',
    included: 'Included',
    subtotal: 'Subtotal',
    discount: 'Discount',
    net: 'Net (before VAT)',
    vat: 'VAT {{rate}}%',
    total: 'Total due',
    notes: 'Notes',
    terms: 'Terms & agreement',
    approvedStamp: 'Approved by the customer on {{date}}',
    signedBy: 'Signed by',
    signedAt: 'Signed at',
    generatedBy: 'Generated by Worki',
    certificateTitle: 'Electronic signature evidence record',
    certificateIntro:
      'This page records the drawn signature and related signing details. It is not a certificate issued by a certification authority and does not establish a certified electronic signature.',
    certificateDocument: 'Document',
    certificateReference: 'Document reference',
    certificateConnection: 'Connection fingerprint',
    certificateDrawing: 'The signature as drawn',
    /* SIG-003 — on a document several people sign, a line each. */
    certificatePending: 'Not signed yet',
    /* 2.5 — worded as on the profile, where the owner typed the number in. */
    taxId: 'Tax ID',
  },
  /* Chrome around every screen: rail, app bar, bottom bar and the small
     controls the ported screens share (row menus, in-page search). */
  shell: {
    navigationToBottom: 'Move navigation to the bottom',
    navigationToSidebar: 'Move navigation to the side',
    navLeads: 'Inquiries',
    navGallery: 'Gallery',
    navInvoicesEmail: 'Invoices from email',
    navInvoicesShort: 'Email invoices',
    navPersonalArea: 'My area',
    new: 'New',
    newAction: 'New action',
    sendHistory: 'Send history',
    sendHistoryShort: 'Send history',
    home: 'Worki — dashboard',
    collapse: 'Collapse the menu',
    frontMenu: 'My menu',
    expand: 'Expand the menu',
    viewAllNotifications: 'See all notifications and reminders',
    search: 'Search',
    searchPlaceholder: 'Search this page…',
    closeSearch: 'Close search',
    view: 'View',
    resend: 'Resend',
    resendCount: 'Resend ({{count}})',
  },
  /* The accent-colour fan in the personal area. Colour names only. */
  theme: {
    title: 'System colour',
    subtitle: 'Pick a primary colour — it applies across every screen',
    color: {
      purple: 'Purple',
      indigo: 'Indigo',
      blue: 'Blue',
      cyan: 'Cyan',
      teal: 'Teal',
      green: 'Green',
      lime: 'Lime',
      gold: 'Gold',
      orange: 'Orange',
      red: 'Red',
      pink: 'Pink',
      slate: 'Graphite',
      violet: 'Vivid violet',
      lavender: 'Lavender',
      fuchsia: 'Fuchsia',
      rose: 'Rose',
      coral: 'Coral',
      amber: 'Amber',
      olive: 'Olive',
      emerald: 'Emerald',
      sky: 'Sky',
      navy: 'Navy',
      steel: 'Steel',
      mocha: 'Mocha',
      plum: 'Plum',
      black: 'Black',
    },
  },
};
